import { useState, useEffect, useRef } from "react";
import { useUiContextStore } from "../../stores/uiContextStore";
import { useToastStore } from "../../stores/toastStore";
import { useNavigate, useParams } from "react-router";
import {
  ArrowLeft,
  ChevronRight,
  ChevronLeft,
  Check,
  Upload,
  CheckCircle2,
  XCircle,
  FileText,
  Trash2,
  ShieldCheck,
  Sparkles,
  FileUp,
  Zap,
  Loader2,
  X,
  Eye,
  MessageSquare,
  Clock,
  Send,
  AlertCircle,
  Info,
  Search,
  Paperclip,
  ClipboardList,
  Link2,
  Calendar,
  CalendarCheck,
  Download,
  Copy,
} from "lucide-react";
import { PageShell } from "../components/layout/PageShell";
import { Breadcrumb } from "../components/Breadcrumb";
import { RBACAction } from "../components/RBACButton";
import { ModalShell } from "../components/Modal";
import { AutomationChecksView } from "../components/AutomationChecksView";
import {
  FilePreviewBody,
  FilePreviewHeader,
} from "../components/FilePreview";
import { DataTable, type Column } from "../components/DataTable";
import { Dropdown } from "../components/Dropdown";
import {
  submitDeclarativeAssessment,
  submitDeclarativeEvidence,
  executeStatement,
  getExecutions,
  getFollowUps,
  respondToFollowUp,
  getAssessmentPlan,
  createSupportingEvidence,
  createSupportingEvidenceWithFile,
  uploadEvidenceFile,
  listSupportingEvidence,
  getSupportingEvidence,
  searchSupportingEvidence,
  linkEvidenceToStatement,
  getLinkedEvidence,
  unlinkEvidence,
  getDeclarativeResults,
  previewSupportingEvidenceFile,
  downloadSupportingEvidenceFile,
  deleteSupportingEvidence,
  getEvidenceByStatement,
  executeAssessmentControls,
  getAssessmentExecutionView,
  finalizeExecution,
  submitForReview,
  getReviewReadiness,
  getAuthHeaders,
  listSspControlImplementations,
  validateEvidenceForCi,
  type DeclarativeStatementPayload,
  type ExecutionResponse,
  type FollowUpItem,
  type ExecutionReviewItem,
  type ApiAssessmentPlan,
  type AssessmentExecutionViewResponse,
  type AssessmentExecutionViewControl,
  type AssessmentExecutionViewStatement,
  type AssessmentExecutionViewIteration,
  type SupportingEvidence,
  type EvidenceLink,
  type EvidenceByStatementItem,
  type ControlExecutionStatusResponse,
  type StatementExecutionPayload,
  type StatementExecutionResponse,
  type EvidenceValidationResult,
} from "../../api/frameworksApi";
import { FollowUpChatModal } from "../components/FollowUpChatModal";
import { FollowUpPanel, FollowUpAction } from "../components/FollowUpPanel";
import { ValidationDetailsDrawer } from "../components/ValidationDetailsDrawer";

/* ── Types ── */

type MethodType = "declarative" | "declarative_evidence" | "automated";
type MethodResult = "yes" | "no" | null;

interface StatementMethod {
  type: MethodType;
  result: MethodResult;
  reason: string;
  evidence: string[];
  autoStatus: "idle" | "running" | "passed" | "failed";
}

interface ExecutionStatement {
  id: string;
  label: string;
  text: string;
  assessmentMethods: string[];
  executionMethod?: "opa" | "declarative" | "declarative_evidence" | null;
  state?: string;
  verdict?: string;
  justification?: string | null;
  /** Assessor's follow-up question — execution-view `note` field. */
  note?: string | null;
  /** Reviewer asked for evidence as part of this follow-up. */
  needsEvidence?: boolean;
  /** Execution-view `review_status`. May say "follow_up_resolved" while
   *  `state` still lags at "FOLLOWUP" — treat it as the resolved truth. */
  reviewStatus?: string | null;
  /** Per-round follow-up timeline (oldest → newest). */
  iterations?: AssessmentExecutionViewIteration[];
  evidenceFiles?: {
    supporting_evidence_id: string;
    title: string;
    file_name: string;
  }[];
  methods: StatementMethod[];
}

interface ExecutionControl {
  id: string;
  controlId: string;
  title: string;
  description: string;
  family: string;
  statements: ExecutionStatement[];
  completed: boolean;
}

/** Attached evidence display item */
interface AttachedEvidence {
  id: string;
  title: string;
  fileName: string | null;
  isNew: boolean; // true = just attached locally, false = already linked on server
}

/** Per-statement form state for declarative+evidence inline UI */
interface DeclEvidenceForm {
  determination: boolean | null;
  evidenceIds: string[];
  justification: string;
}

/** Flattened row for DataTable */
interface ExecRow {
  ctrl: ExecutionControl;
  stmt: ExecutionStatement;
  idx: number;
  kind: "declarative";
}

/** Automation row for DataTable */
interface AutoRow {
  autoCtrl: ControlExecutionStatusResponse;
  idx: number;
  kind: "automation";
}

type TableRow = ExecRow | AutoRow;

/* ── Helpers ── */
const countWords = (text: string) =>
  text.trim().split(/\s+/).filter(Boolean).length;
const MIN_WORDS = 20;

/* For an automated re-save the system owner's reply is stored as the statement's
 * justification (not as a followUpResponse in reviewHistory). When the history has
 * no owner response yet but the statement is resolved with a justification, append
 * it as a response entry so the chat shows both sides. */
const buildChatHistory = (
  base: ExecutionReviewItem["reviewHistory"],
  exec: ExecutionReviewItem["execution"] | undefined,
  status: string | undefined,
): ExecutionReviewItem["reviewHistory"] => {
  if (!exec) return base;
  const sentinels = new Set(["accepted", "rejected", "approved"]);
  const hasOwnerResponse = base.some(
    (h) =>
      h.followUpResponse &&
      !sentinels.has(h.followUpResponse.trim().toLowerCase()),
  );
  const justification = exec.justification?.trim();
  if (hasOwnerResponse || !justification || status !== "follow_up_resolved")
    return base;
  return [
    ...base,
    {
      id: `${exec.id}-owner-resp`,
      reviewStatus: "follow_up_resolved",
      followUpQuestion: null,
      followUpResponse: justification,
      followUpRespondedBy: null,
      followUpRespondedAt:
        exec.updatedAt ||
        exec.executedAt ||
        exec.createdAt ||
        new Date().toISOString(),
      reviewedBy: "",
      reviewedAt: exec.updatedAt || "",
    },
  ];
};

/* ── Method display config ── */

const METHOD_CONFIG: Record<
  MethodType,
  { label: string; description: string; icon: React.ElementType }
> = {
  declarative: {
    label: "Declarative",
    description: "Provide a Yes/No answer with justification",
    icon: ShieldCheck,
  },
  declarative_evidence: {
    label: "Declarative + Evidence",
    description: "Provide a Yes/No answer with justification and evidence",
    icon: FileUp,
  },
  automated: {
    label: "Automated",
    description: "Run automated validation against the control",
    icon: Zap,
  },
};

/* ── Helper: map API control statuses → ExecutionControl[] ── */
function mapStatusesToControls(
  statuses: ControlExecutionStatusResponse[],
): ExecutionControl[] {
  return statuses
    .filter(
      (s) =>
        s.controlType === "manual" ||
        (s.controlType === "automated" && (s.opaResults ?? []).length === 0),
    )
    .map((c, idx) => ({
      id: c.planControlId || `ec-${idx}`,
      controlId: c.controlId,
      title: c.controlId,
      description: c.controlDescription ?? "",
      family: c.controlId.split("-")[0] || "",
      completed: false,
      statements: (c.statements ?? []).map((s, sIdx) => {
        const methods: StatementMethod[] = [
          {
            type: "declarative",
            result: null,
            reason: "",
            evidence: [],
            autoStatus: "idle",
          },
          {
            type: "declarative_evidence",
            result: null,
            reason: "",
            evidence: [],
            autoStatus: "idle",
          },
        ];
        return {
          id: s.id || `stmt-${idx}-${sIdx}`,
          label: s.statementId,
          text: s.statementDescription,
          assessmentMethods: s.assessmentMethods ?? [],
          executionMethod: s.executionMethod ?? null,
          state: s.state,
          verdict: s.verdict,
          justification: s.justification ?? null,
          note: s.note ?? null,
          needsEvidence: Boolean(s.needsEvidence),
          reviewStatus: s.reviewStatus ?? null,
          iterations: s.iterations ?? [],
          evidenceFiles: s.evidenceFiles ?? [],
          methods,
        };
      }),
    }));
}

export const normalizeExecutionStatus = (status?: string | null) => {
  const value = (status ?? "").toLowerCase();
  if (value.includes("pass")) return "passed";
  if (value.includes("fail")) return "failed";
  if (value.includes("pending")) return "pending";
  return value || "pending";
};

export const resolveExecutionViewVerdict = (
  control: AssessmentExecutionViewControl,
) => {
  const statuses = control.statements.flatMap((statement) =>
    (statement.assets ?? []).flatMap((asset) =>
      (asset.controls ?? []).map((tech) =>
        normalizeExecutionStatus(tech.status),
      ),
    ),
  );

  if (statuses.some((status) => status === "failed")) return "failed";
  if (statuses.length > 0 && statuses.every((status) => status === "passed")) {
    return "passed";
  }

  const verdicts = control.statements.map((statement) =>
    normalizeExecutionStatus(statement.verdict),
  );
  if (verdicts.some((status) => status === "failed")) return "failed";
  if (verdicts.length > 0 && verdicts.every((status) => status === "passed")) {
    return "passed";
  }
  return "pending";
};

export const mapExecutionViewToStatuses = (
  view: AssessmentExecutionViewResponse,
): ControlExecutionStatusResponse[] =>
  (view.controls ?? []).map((control, index) => {
    const isAutomated = control.mode?.toUpperCase() === "AUTOMATED";
    const executionViewAssets = control.statements.flatMap(
      (statement) => statement.assets ?? [],
    );
    const opaResults = isAutomated
      ? executionViewAssets.flatMap((asset) =>
          (asset.controls ?? []).map((tech) => ({
            opaControlId: tech.tech_control_id,
            catalogName: asset.component_name || control.mode,
            controlStatus: normalizeExecutionStatus(tech.status),
            evidence: tech.evidence_data
              ? [{ evidence_data: tech.evidence_data }]
              : [],
            assetUuid: asset.asset_uuid,
            assetName: asset.asset_hostname,
            assetIdentifierType: asset.component_name,
          })),
        )
      : [];

    return {
      planControlId: control.plan_control_id,
      controlId: control.control_id,
      controlDescription: control.control_description,
      statements: (control.statements ?? []).map((statement, sIdx) => ({
        id: statement.statement_id || `${control.plan_control_id}-${sIdx}`,
        statementId:
          statement.statement_label ||
          statement.statement_id ||
          `${control.control_id}-statement-${sIdx + 1}`,
        statementDescription: statement.statement_description,
        assessmentMethods: statement.needs_evidence
          ? ["declarative_evidence", ...(statement.assessment_methods ?? [])]
          : (statement.assessment_methods ?? []),
        sortOrder: sIdx,
        createdAt: "",
        updatedAt: "",
        executionMethod: statement.execution_method ?? null,
        state: statement.state,
        verdict: statement.verdict,
        justification: statement.justification ?? null,
        note: statement.note ?? statement.reviewer_note ?? null,
        needsEvidence: Boolean(statement.needs_evidence),
        reviewStatus: statement.review_status ?? null,
        iterations: statement.iterations ?? [],
        evidenceFiles: statement.evidence_files ?? [],
      })),
      sortOrder: index,
      implementationStatus: null,
      controlType: isAutomated ? "automated" : "manual",
      executable: isAutomated,
      executed: isAutomated,
      followUpPending: false,
      followUpQuestion: null,
      opaOverallStatus: isAutomated
        ? resolveExecutionViewVerdict(control)
        : null,
      opaResults,
      determination: null,
      justification: null,
      executedAt: null,
      executionMode: control.mode,
      executionViewAssets,
      executionViewStatements: control.statements ?? [],
    };
  });

/* ── Component ── */

export function AssessmentExecution() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [controlsState, setControlsState] = useState<ExecutionControl[]>([]);
  const [planInfo, setPlanInfo] = useState<ApiAssessmentPlan | null>(null);
  // Publish the plan under execution, so {item} questions in assessment.yaml apply. Cleared on unmount.
  useEffect(() => {
    const title = planInfo?.title;
    useUiContextStore
      .getState()
      .setSelectedItem(id && title ? { id, name: title } : null);
    return () => useUiContextStore.getState().clearSelectedItem();
  }, [id, planInfo]);
  // Server-driven flag for the "All Controls Completed" overlay.
  // GET /assessment-plans/{planId}/review-readiness — primary source of truth.
  // Falls back to a local flag set after the user successfully clicks
  // "Move to Review" when the BE endpoint is not yet available (e.g. 404).
  const [allReviewed, setAllReviewed] = useState(false);
  const [submittedForReview, setSubmittedForReview] = useState(false);

  const refreshReviewReadiness = async () => {
    if (!id) return;
    try {
      const res = await getReviewReadiness(id);
      setAllReviewed(Boolean(res.data?.allReviewed));
    } catch {
      // BE endpoint missing/errored — leave allReviewed alone; the
      // submittedForReview fallback below will drive the overlay instead.
    }
  };
  const [planName, setPlanName] = useState("");
  const [sspName, setSspName] = useState("");
  const [owningOrgUnitId, setOwningOrgUnitId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  /* ── 3-Panel tree state ── */
  const [treeSearch, setTreeSearch] = useState("");
  const [treeFilter, setTreeFilter] = useState<
    "all" | "pending" | "completed" | "follow_up"
  >("all");
  const [expandedTreeIds, setExpandedTreeIds] = useState<Set<string>>(
    new Set(),
  );
  const [evidencePanelTab, setEvidencePanelTab] = useState<
    "existing" | "upload"
  >("existing");

  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const CONTROLS_PER_PAGE = 10;
  const [tablePage, setTablePage] = useState(0);
  const [tablePageSize, setTablePageSize] = useState(10);
  const [tableSearch, setTableSearch] = useState("");
  const [tableKindFilter, setTableKindFilter] = useState<
    "all" | "not_yet_assessed" | "declarative" | "declarative_evidence" | "automation"
  >("all");
  const [tableStatusFilter, setTableStatusFilter] = useState<Set<string>>(
    () => new Set(["satisfied", "not_satisfied", "completed", "not_completed"]),
  );
  const [tableSortKey, setTableSortKey] = useState<string | null>(null);
  const [tableSortDir, setTableSortDir] = useState<"asc" | "desc">("asc");
  const handleTableSort = (key: string) => {
    if (tableSortKey === key) {
      // Cycle: asc -> desc -> unsorted (back to original order)
      if (tableSortDir === "asc") {
        setTableSortDir("desc");
      } else {
        setTableSortKey(null);
        setTableSortDir("asc");
      }
    } else {
      setTableSortKey(key);
      setTableSortDir("asc");
    }
    setTablePage(0);
  };

  /* View modal (executed statements — shows justification + evidence files) */
  const [viewModalStmt, setViewModalStmt] = useState<ExecutionStatement | null>(
    null,
  );
  const [viewModalCtrl, setViewModalCtrl] = useState<ExecutionControl | null>(
    null,
  );

  /* Evidence-validation drawer state (AI validation of evidence vs control) */
  const [validationDrawerOpen, setValidationDrawerOpen] = useState(false);
  const [validationLoading, setValidationLoading] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [validationData, setValidationData] =
    useState<EvidenceValidationResult | null>(null);
  const [validationRowKey, setValidationRowKey] = useState<string | null>(null);
  const [validationMeta, setValidationMeta] = useState<{
    controlId: string;
    controlTitle?: string;
    evidenceName?: string;
  }>({ controlId: "" });

  /* Modal state for declarative popup */
  const [assessModalOpen, setAssessModalOpen] = useState(false);
  const [followUpModalOpen, setFollowUpModalOpen] = useState(false);
  const [followUpModalExecId, setFollowUpModalExecId] = useState<string | null>(
    null,
  );
  const [followUpModalStmtLabel, setFollowUpModalStmtLabel] = useState<
    string | null
  >(null);
  const [followUpModalControlId, setFollowUpModalControlId] = useState<
    string | null
  >(null);
  const [followUpModalSource, setFollowUpModalSource] = useState<
    "assess" | "auto" | null
  >(null);
  const [followUpModalQuestion, setFollowUpModalQuestion] = useState<
    string | null
  >(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalCtrlId, setModalCtrlId] = useState<string | null>(null);
  const [modalStmtId, setModalStmtId] = useState<string | null>(null);
  const [modalTab, setModalTab] = useState<
    "declarative" | "declarative_evidence"
  >("declarative");
  /** Wizard step for Declarative+Evidence tab: 1 = determination, 2 = evidence */
  const [deEvStep, setDeEvStep] = useState<1 | 2>(1);
  const [deEvSaveLoading, setDeEvSaveLoading] = useState(false);

  const openDeclarativeModal = (
    ctrlId: string,
    stmtId: string,
    tab: "declarative" | "declarative_evidence" = "declarative",
  ) => {
    setModalCtrlId(ctrlId);
    setModalStmtId(stmtId);
    setModalTab(tab);
    setDeEvStep(1);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setModalCtrlId(null);
    setModalStmtId(null);
    setDeEvStep(1);
    setDeEvSelectedFiles([]);
    setDeEvSelectedExisting(new Set());
    setDeEvExistingSearch("");
  };

  /* ── Automation state ── */
  /* ── Control statuses (all controls: manual + automated) ── */
  const [controlStatuses, setControlStatuses] = useState<
    ControlExecutionStatusResponse[]
  >([]);

  /* ── Automation state ── */
  const [autoModalOpen, setAutoModalOpen] = useState(false);
  const [autoModalCtrl, setAutoModalCtrl] =
    useState<ControlExecutionStatusResponse | null>(null);
  /* Follow-up re-save (automation): selected evidence file + in-flight flag */
  const [autoFuFile, setAutoFuFile] = useState<File | null>(null);
  const [autoFuSaving, setAutoFuSaving] = useState(false);
  /* Whether the in-modal "Respond to Follow-Up" card is expanded. Opens by
   * default whenever the modal is opened on a follow-up control so the input is
   * visible without an extra click; Cancel collapses it back to the prompt. */
  const [autoRespondOpen, setAutoRespondOpen] = useState(true);
  /* Declarative / D+E inline follow-up response form state (View modal).
   * Mirrors the automation pattern: expanded textarea + evidence picker
   * (when needs_evidence) + word counter, submitted via respondToFollowUp.
   * The text/evidence values are stored in followUpResponses /
   * followUpEvidenceIds keyed by statement id so the existing focused
   * modal can share state if reached. */
  const [declFuRespondOpen, setDeclFuRespondOpen] = useState(true);
  const [declFuSaving, setDeclFuSaving] = useState(false);
  useEffect(() => {
    if (autoModalOpen) setAutoRespondOpen(true);
  }, [autoModalOpen]);
  // Reset the declarative inline respond form whenever a different
  // statement is opened in the View modal — keeps stale UI state from
  // leaking between statements. (Text/evidence are keyed by statement id
  // in followUpResponses/followUpEvidenceIds and persist across opens.)
  useEffect(() => {
    setDeclFuRespondOpen(true);
  }, [viewModalStmt?.id]);
  /* Justification the system owner adds when re-submitting a followed-up control. */
  const [autoFuJustification, setAutoFuJustification] = useState("");
  const AUTO_OPA_PER_PAGE = 3;
  const OPA_PAGE_SIZE = 5;

  const [autoOpaPage, setAutoOpaPage] = useState(0);
  /* Automation Results modal: per-check filter / search / page size.
   * Handles controls with many automated checks (hundreds of asset × tech rows). */
  const [autoChecksFilter, setAutoChecksFilter] = useState<
    "all" | "passed" | "failed" | "pending"
  >("all");
  const [autoChecksSearch, setAutoChecksSearch] = useState("");
  const [autoRowsPerPage, setAutoRowsPerPage] = useState(25);
  // Reset filter/search/page whenever a different control's modal is opened.
  useEffect(() => {
    setAutoOpaPage(0);
    setAutoChecksFilter("all");
    setAutoChecksSearch("");
  }, [autoModalCtrl?.planControlId]);

  const [violationPages, setViolationPages] = useState<Record<string, number>>(
    {},
  );
  const [evidencePages, setEvidencePages] = useState<Record<string, number>>(
    {},
  );

  /* ── Evidence state ── */
  const [evidenceLinks, setEvidenceLinks] = useState<EvidenceLink[]>([]);
  const [evidenceCache, setEvidenceCache] = useState<
    Record<string, SupportingEvidence>
  >({});
  const [evidenceSubTab, setEvidenceSubTab] = useState<"upload" | "existing">(
    "upload",
  );
  const [evidenceSearch, setEvidenceSearch] = useState("");
  const [evidenceSearchResults, setEvidenceSearchResults] = useState<
    SupportingEvidence[]
  >([]);
  const [evidenceSearchLoading, setEvidenceSearchLoading] = useState(false);
  const [evidenceTitle, setEvidenceTitle] = useState("");
  const [evidenceTags, setEvidenceTags] = useState("");
  const [evidenceUploading, setEvidenceUploading] = useState(false);
  const [evidenceLinking, setEvidenceLinking] = useState(false);
  /** Files selected in Step 2 upload zone (not yet uploaded) */
  const [deEvSelectedFiles, setDeEvSelectedFiles] = useState<File[]>([]);
  /** Existing evidence IDs selected from list in Step 2 */
  const [deEvSelectedExisting, setDeEvSelectedExisting] = useState<Set<string>>(new Set());
  /** Search query for existing evidence list in Step 2 */
  const [deEvExistingSearch, setDeEvExistingSearch] = useState("");
  /** Preview modal for an evidence file, or for a file picked in the upload
   *  zone but not yet uploaded (then `url` is a local blob URL). The shared
   *  preview engine (components/FilePreview) fetches, detects the real file
   *  type and renders PDFs, images, text and Word documents inline. */
  const [preview, setPreview] = useState<{
    url: string;
    title: string;
    fileName: string | null;
    downloadUrl: string;
  } | null>(null);

  /** Release the object URL of a not-yet-uploaded local file preview. */
  const revokeLocalPreview = (p: { url: string } | null) => {
    if (p?.url.startsWith("blob:")) URL.revokeObjectURL(p.url);
  };

  /** Open preview for stored evidence */
  const handlePreview = (evidenceId: string, title: string) => {
    if (!evidenceId) return;
    setPreview((prev) => {
      revokeLocalPreview(prev);
      return {
        url: previewSupportingEvidenceFile(evidenceId),
        title,
        fileName: null,
        downloadUrl: downloadSupportingEvidenceFile(evidenceId),
      };
    });
  };

  /** Close preview and release any local blob URL */
  const closePreview = () => {
    setPreview((prev) => {
      revokeLocalPreview(prev);
      return null;
    });
  };

  /** Download evidence file with authentication */
  const handleDownload = async (evidenceId: string, fileName?: string) => {
    try {
      const url = downloadSupportingEvidenceFile(evidenceId);
      const res = await fetch(url, {
        headers: getAuthHeaders(),
      });
      if (!res.ok) throw new Error("File not available");
      const contentType = res.headers.get("content-type") || "";
      if (
        contentType.includes("application/xml") ||
        contentType.includes("text/xml")
      ) {
        throw new Error("File not available");
      }
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = fileName || `evidence-${evidenceId}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error("Download failed:", err);
      useToastStore
        .getState()
        .showError("Error", "File is not available for this evidence.");
    }
  };

  /** Delete confirmation state */
  const [deleteConfirm, setDeleteConfirm] = useState<{
    stmtId: string;
    stmtLabel: string;
    evidenceId: string;
    evidenceTitle: string;
  } | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  /** Show delete confirmation dialog */
  const confirmDeleteEvidence = (
    stmtId: string,
    stmtLabel: string,
    evidenceId: string,
    evidenceTitle: string,
  ) => {
    setDeleteConfirm({ stmtId, stmtLabel, evidenceId, evidenceTitle });
  };

  /** Delete evidence entry from server and remove from local state */
  const handleDeleteEvidence = async () => {
    if (!id || !deleteConfirm) return;
    const { stmtId, stmtLabel, evidenceId } = deleteConfirm;
    setDeleteLoading(true);
    try {
      // Unlink from statement first
      await unlinkEvidence(id, evidenceId, stmtLabel);
      // Delete the evidence entry itself
      await deleteSupportingEvidence(evidenceId);
      // Update local state
      setEvidenceLinks((prev) =>
        prev.filter(
          (l) =>
            !(
              l.supportingEvidenceId === evidenceId &&
              l.statementId === stmtLabel
            ),
        ),
      );
      setDeclEvAttached((prev) => ({
        ...prev,
        [stmtId]: (prev[stmtId] ?? []).filter((e) => e.id !== evidenceId),
      }));
      updateDeclEvForm(stmtId, {
        evidenceIds: getDeclEvForm(stmtId).evidenceIds.filter(
          (eid) => eid !== evidenceId,
        ),
      });
      setDeleteConfirm(null);
    } catch (err: any) {
      const apiMsg =
        err?.responseBody?.errors?.[0]?.detail ||
        err?.responseBody?.errors?.[0]?.title ||
        err?.message;
      useToastStore
        .getState()
        .showError("Error", apiMsg || "Failed to delete evidence");
    } finally {
      setDeleteLoading(false);
    }
  };

  /* ── Declarative + Evidence inline form state ── */
  /** Per-statement form data keyed by statementId */
  const [declEvForms, setDeclEvForms] = useState<
    Record<string, DeclEvidenceForm>
  >({});
  /** Locally attached evidence per statement (includes both server-linked and new) */
  const [declEvAttached, setDeclEvAttached] = useState<
    Record<string, AttachedEvidence[]>
  >({});
  /** Selected evidence IDs in "Attach Existing" modal (checkbox state) */
  const [attachSelectedIds, setAttachSelectedIds] = useState<Set<string>>(
    new Set(),
  );

  /* ── Attach Existing modal ── */
  const [attachModalOpen, setAttachModalOpen] = useState(false);
  const [attachModalStmtId, setAttachModalStmtId] = useState<string | null>(
    null,
  );
  const [attachModalStmtLabel, setAttachModalStmtLabel] = useState<
    string | null
  >(null);
  const [attachModalCtrlId, setAttachModalCtrlId] = useState<string | null>(
    null,
  );

  /* ── Upload New modal ── */
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [uploadModalStmtId, setUploadModalStmtId] = useState<string | null>(
    null,
  );
  const [uploadModalStmtLabel, setUploadModalStmtLabel] = useState<
    string | null
  >(null);
  const [uploadModalCtrlId, setUploadModalCtrlId] = useState<string | null>(
    null,
  );

  /** Initialize or get decl+evidence form for a statement */
  const getDeclEvForm = (stmtId: string): DeclEvidenceForm => {
    return (
      declEvForms[stmtId] ?? {
        determination: null,
        evidenceIds: [],
        justification: "",
      }
    );
  };

  const updateDeclEvForm = (
    stmtId: string,
    updates: Partial<DeclEvidenceForm>,
  ) => {
    setDeclEvForms((prev) => ({
      ...prev,
      [stmtId]: { ...getDeclEvForm(stmtId), ...updates },
    }));
  };

  /** Open Attach Existing modal */
  const openAttachModal = (
    ctrlId: string,
    stmtId: string,
    stmtLabel: string,
  ) => {
    setAttachModalCtrlId(ctrlId);
    setAttachModalStmtId(stmtId);
    setAttachModalStmtLabel(stmtLabel);
    setAttachSelectedIds(new Set());
    setEvidenceSearch("");
    setAttachModalOpen(true);
    searchEvidence("");
  };

  /** Open Upload New modal */
  const openUploadModal = (
    ctrlId: string,
    stmtId: string,
    stmtLabel: string,
  ) => {
    setUploadModalCtrlId(ctrlId);
    setUploadModalStmtId(stmtId);
    setUploadModalStmtLabel(stmtLabel);
    setEvidenceTitle("");
    setEvidenceTags("");
    setUploadModalOpen(true);
  };

  /** Get evidence IDs linked to a specific statement */
  const getStatementEvidenceIds = (stmtLabel: string): string[] => {
    return evidenceLinks
      .filter((l) => l.statementId === stmtLabel)
      .map((l) => l.supportingEvidenceId);
  };

  /** Fetch linked evidence for the plan. Evidence details (titles, fileName)
   *  are loaded lazily by the D+E / attach modal flows. */
  const fetchLinkedEvidence = async () => {
    if (!id) return;
    try {
      const res = await getLinkedEvidence(id, "assessment_execution");
      setEvidenceLinks(res.data ?? []);
    } catch {
      /* ignore */
    }
  };

  /** Resolve the first evidence attached to a row — checks the execution-view
   *  evidence, the D+E local attachments, then the plan-wide evidence links. */
  const resolveRowEvidence = (
    row: TableRow,
  ): { id: string; name?: string } | null => {
    if (row.kind === "declarative") {
      const fromStmt = row.stmt.evidenceFiles?.[0];
      if (fromStmt?.supporting_evidence_id) {
        return {
          id: fromStmt.supporting_evidence_id,
          name: fromStmt.title || fromStmt.file_name,
        };
      }
      const attached = declEvAttached[row.stmt.id]?.[0];
      if (attached?.id) {
        return { id: attached.id, name: attached.title };
      }
      const linked = evidenceLinks.find(
        (l) => l.statementId === row.stmt.label,
      );
      if (linked?.supportingEvidenceId) {
        return {
          id: linked.supportingEvidenceId,
          name: linked.evidenceName ?? undefined,
        };
      }
      return null;
    }
    const autoStmt = (row.autoCtrl.statements ?? []).find(
      (s) => (s.evidenceFiles ?? []).length > 0,
    );
    const autoEv = autoStmt?.evidenceFiles?.[0];
    if (autoEv?.supporting_evidence_id) {
      return {
        id: autoEv.supporting_evidence_id,
        name: autoEv.title || autoEv.file_name,
      };
    }
    return null;
  };

  const rowValidationKey = (row: TableRow) =>
    row.kind === "declarative"
      ? `stmt:${row.stmt.id}`
      : `auto:${row.autoCtrl.controlId}`;

  /** Handler: run evidence validation for a row and open the drawer with results. */
  const handleValidateEvidence = async (row: TableRow) => {
    // importSspHref is a full OSCAL href (e.g. "/api/v1/ssps/{uuid}") — take
    // the trailing UUID segment. sspName may already be a bare UUID from the
    // execution-view fallback.
    const rawSspRef = planInfo?.importSspHref || sspName;
    const sspId = rawSspRef ? (rawSspRef.split("/").pop() ?? rawSspRef) : "";
    if (!sspId) {
      useToastStore
        .getState()
        .showError("Validation Unavailable", "SSP is not linked to this plan.");
      return;
    }
    const evidence = resolveRowEvidence(row);
    if (!evidence) {
      useToastStore
        .getState()
        .showError(
          "No Evidence Attached",
          "Attach evidence to this control before running validation.",
        );
      return;
    }
    const controlId =
      row.kind === "declarative" ? row.ctrl.controlId : row.autoCtrl.controlId;
    const controlStatement =
      row.kind === "declarative"
        ? row.stmt.text
        : (row.autoCtrl.statements ?? [])
            .map((s) => s.statementDescription)
            .filter(Boolean)
            .join("; ") || row.autoCtrl.controlDescription || "";
    const controlTitle =
      row.kind === "declarative"
        ? row.ctrl.description || row.ctrl.controlId
        : row.autoCtrl.controlDescription || row.autoCtrl.controlId;

    const rowKey = rowValidationKey(row);
    setValidationRowKey(rowKey);
    setValidationMeta({
      controlId: controlId?.toUpperCase() ?? "",
      controlTitle,
      evidenceName: evidence.name,
    });
    setValidationData(null);
    setValidationError(null);
    setValidationLoading(true);
    setValidationDrawerOpen(true);

    try {
      // Resolve ciId + implementedRequirementId (+ componentId, when present)
      // from the SSP's control-implementations. The list is already filtered
      // by control_id server-side; prefer a row that carries a componentId
      // but fall back to the first entry so the validation can still run.
      const ciList = await listSspControlImplementations(sspId, controlId);
      const rows = ciList.data ?? [];
      const norm = (s?: string | null) => (s ?? "").toLowerCase();
      const target = norm(controlId);
      const byControl = rows.filter((ci) => norm(ci.controlId) === target);
      const pool = byControl.length ? byControl : rows;
      const match = pool.find((ci) => !!ci.componentId) ?? pool[0];
      if (!match?.ciId || !match?.id) {
        throw new Error(
          "Could not resolve the SSP control implementation for this control.",
        );
      }
      const res = await validateEvidenceForCi(sspId, match.ciId, {
        implementedRequirementId: match.id,
        componentId: match.componentId ?? "",
        evidenceId: evidence.id,
        orgId: owningOrgUnitId ?? "",
        controlId,
        controlStatement: controlStatement ?? "",
      });
      setValidationData(res.data ?? null);
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : "Validation request failed. Please try again.";
      setValidationError(message);
      useToastStore.getState().showError("Validation Failed", message);
    } finally {
      setValidationLoading(false);
    }
  };

  const closeValidationDrawer = () => {
    setValidationDrawerOpen(false);
    setValidationLoading(false);
    setValidationRowKey(null);
  };

  /** Search or list evidence */
  const searchEvidence = async (query: string) => {
    setEvidenceSearchLoading(true);
    try {
      const res = query.trim()
        ? await searchSupportingEvidence(query)
        : await listSupportingEvidence();
      const items = res.data ?? [];
      setEvidenceSearchResults(items);
      // Cache them
      const newCache = { ...evidenceCache };
      items.forEach((e) => {
        newCache[e.id] = e;
      });
      setEvidenceCache(newCache);
    } catch {
      setEvidenceSearchResults([]);
    }
    setEvidenceSearchLoading(false);
  };

  /** Upload new evidence: create entry → upload file → attach locally to statement */
  const handleUploadNewEvidence = async (file: File) => {
    // Support both: separate upload modal OR inline step-2 upload inside main modal
    const stmtId = uploadModalStmtId || modalStmtId;
    const stmtLabel =
      uploadModalStmtLabel ||
      (() => {
        if (!modalCtrlId || !modalStmtId) return null;
        const ctrl = controlsState.find((c) => c.id === modalCtrlId);
        return (
          ctrl?.statements?.find((s) => s.id === modalStmtId)?.label ?? null
        );
      })();
    if (!id || !stmtId || !stmtLabel) return;

    setEvidenceUploading(true);
    try {
      const title = evidenceTitle.trim() || file.name;
      const tags = evidenceTags.trim()
        ? evidenceTags
            .split(",")
            .map((t) => t.trim())
            .filter(Boolean)
        : [];
      const createRes = await createSupportingEvidence({
        title,
        tags,
        owningOrgUnitId: owningOrgUnitId || undefined,
      });
      const evidence = createRes.data;

      const uploadRes = await uploadEvidenceFile(evidence.id, file);
      const updatedEvidence = uploadRes.data;

      // Cache evidence details
      setEvidenceCache((prev) => ({
        ...prev,
        [updatedEvidence.id]: updatedEvidence,
      }));

      // If uploading from step 2 (main modal), link to statement via API immediately
      const isStep2 = !uploadModalStmtId && modalStmtId;
      if (isStep2) {
        await linkEvidenceToStatement(id, {
          statementId: stmtLabel,
          evidenceIds: [updatedEvidence.id],
        });
      }

      // Add to local attached list
      setDeclEvAttached((prev) => ({
        ...prev,
        [stmtId]: [
          ...(prev[stmtId] ?? []),
          {
            id: updatedEvidence.id,
            title: updatedEvidence.title,
            fileName: updatedEvidence.fileName,
            isNew: !isStep2,
          },
        ],
      }));

      // Also add to form evidenceIds
      updateDeclEvForm(stmtId, {
        evidenceIds: [...getDeclEvForm(stmtId).evidenceIds, updatedEvidence.id],
      });

      setEvidenceTitle("");
      setEvidenceTags("");
      if (uploadModalStmtId) setUploadModalOpen(false);
    } catch (err) {
      console.error("Evidence upload failed:", err);
    }
    setEvidenceUploading(false);
  };

  /** Attach selected existing evidence to a statement (from Attach Existing modal) */
  const handleAttachSelectedEvidence = () => {
    if (!attachModalStmtId || attachSelectedIds.size === 0) return;
    const stmtId = attachModalStmtId;
    const currentForm = getDeclEvForm(stmtId);
    const currentAttached = declEvAttached[stmtId] ?? [];
    const existingIds = new Set(currentAttached.map((e) => e.id));

    const newAttachments: AttachedEvidence[] = [];
    const newIds: string[] = [];
    attachSelectedIds.forEach((evId) => {
      if (!existingIds.has(evId)) {
        const ev = evidenceCache[evId];
        newAttachments.push({
          id: evId,
          title: ev?.title || `Evidence ${evId.slice(0, 8)}`,
          fileName: ev?.fileName ?? null,
          isNew: true,
        });
        newIds.push(evId);
      }
    });

    if (newAttachments.length > 0) {
      setDeclEvAttached((prev) => ({
        ...prev,
        [stmtId]: [...(prev[stmtId] ?? []), ...newAttachments],
      }));
      updateDeclEvForm(stmtId, {
        evidenceIds: [...currentForm.evidenceIds, ...newIds],
      });
    }

    setAttachModalOpen(false);
    setAttachSelectedIds(new Set());
  };

  /** Remove evidence from a statement */
  const handleRemoveEvidence = async (
    stmtId: string,
    stmtLabel: string,
    evidenceId: string,
    isNew: boolean,
  ) => {
    if (!isNew && id) {
      // Already linked on server - call DELETE API
      try {
        await unlinkEvidence(id, evidenceId, stmtLabel);
        setEvidenceLinks((prev) =>
          prev.filter(
            (l) =>
              !(
                l.supportingEvidenceId === evidenceId &&
                l.statementId === stmtLabel
              ),
          ),
        );
      } catch (err) {
        console.error("Evidence unlink failed:", err);
        return;
      }
    }

    // Remove from local state
    setDeclEvAttached((prev) => ({
      ...prev,
      [stmtId]: (prev[stmtId] ?? []).filter((e) => e.id !== evidenceId),
    }));
    updateDeclEvForm(stmtId, {
      evidenceIds: getDeclEvForm(stmtId).evidenceIds.filter(
        (eid) => eid !== evidenceId,
      ),
    });
  };

  const getModalStatement = (): ExecutionStatement | null => {
    if (!modalCtrlId || !modalStmtId) return null;
    const ctrl = controlsState.find((c) => c.id === modalCtrlId);
    if (!ctrl) return null;
    return ctrl.statements.find((s) => s.id === modalStmtId) ?? null;
  };

  const getModalControl = (): ExecutionControl | null => {
    if (!modalCtrlId) return null;
    return controlsState.find((c) => c.id === modalCtrlId) ?? null;
  };

  useEffect(() => {
    if (error) useToastStore.getState().showError("Error", error);
  }, [error]);

  /* ── Fetch pending controls from API ── */
  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setReviewStatusLoaded(false);

    Promise.all([
      getAssessmentPlan(id).catch(() => ({
        data: null as ApiAssessmentPlan | null,
      })),
      getLinkedEvidence(id, "assessment_execution").catch(() => ({
        data: [] as EvidenceLink[],
      })),
      getAssessmentExecutionView(id).then((res) => ({
        data: mapExecutionViewToStatuses(res.data),
        executionView: res.data,
      })),
    ])
      .then(([planRes, evidenceRes, statusesRes]) => {
        if (cancelled) return;
        const plan = planRes.data;
        setPlanInfo(plan);
        setPlanName(plan?.title ?? "Assessment Execution");
        setSspName(
          plan?.importSspHref ?? statusesRes.executionView?.ssp_id ?? "",
        );
        setOwningOrgUnitId(plan?.owningOrgUnitId ?? null);

        setAllReviewed(false);

        const statuses = statusesRes.data ?? [];
        setControlStatuses(statuses);

        const allMapped = mapStatusesToControls(statuses);
        setControlsState(allMapped);

        const links = evidenceRes.data ?? [];
        setEvidenceLinks(links);

        // Initialize declEvAttached from server-linked evidence.
        // The link response carries evidenceName, so the title renders
        // immediately without needing the full evidence list.
        const attached: Record<string, AttachedEvidence[]> = {};
        for (const link of links) {
          for (const ctrl of allMapped) {
            const stmt = ctrl.statements.find(
              (s) => s.label === link.statementId,
            );
            if (stmt) {
              if (!attached[stmt.id]) attached[stmt.id] = [];
              attached[stmt.id].push({
                id: link.supportingEvidenceId,
                title:
                  link.evidenceName ||
                  `Evidence ${link.supportingEvidenceId.slice(0, 8)}`,
                fileName: null,
                isNew: false,
              });
              break;
            }
          }
        }
        setDeclEvAttached(attached);

        // Initialize declEvForms with existing evidence IDs
        const forms: Record<string, DeclEvidenceForm> = {};
        for (const [stmtId, evList] of Object.entries(attached)) {
          forms[stmtId] = {
            determination: null,
            evidenceIds: evList.map((e) => e.id),
            justification: "",
          };
        }
        setDeclEvForms(forms);
        setReviewStatusLoaded(true);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("Failed to load pending controls:", err);
        setError("Failed to load controls. Please try again.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [id]);

  /* Derived */
  const completedCount = controlsState.filter((c) => c.completed).length;
  const totalCount = controlsState.length;
  const progressPct = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;
  const hasAutomatedControls = controlStatuses.some(
    (s) => s.controlType === "automated" && (s.opaResults ?? []).length > 0,
  );
  const allAutoOnly = totalCount === 0 && hasAutomatedControls;
  const allCompleted =
    allAutoOnly || (totalCount > 0 && completedCount === totalCount);
  /* Move to Review may only fire once every control has no follow-up pending.
   * Source of truth: /control-statuses. */
  const anyFollowUpPending = controlStatuses.some(
    (s) => s.followUpPending === true,
  );

  /* Sort: incomplete first, completed last */
  const sortedControls = [...controlsState].sort((a, b) => {
    if (a.completed === b.completed) return 0;
    return a.completed ? 1 : -1;
  });

  const totalPages = Math.ceil(sortedControls.length / CONTROLS_PER_PAGE);
  const pagedControls = sortedControls.slice(
    page * CONTROLS_PER_PAGE,
    (page + 1) * CONTROLS_PER_PAGE,
  );

  /* ── Helpers ── */

  const updateMethod = (
    controlId: string,
    stmtId: string,
    methodType: MethodType,
    updates: Partial<StatementMethod>,
  ) => {
    setControlsState((prev) =>
      prev.map((c) => {
        if (c.id !== controlId) return c;
        return {
          ...c,
          statements: c.statements.map((s) => {
            if (s.id !== stmtId) return s;
            return {
              ...s,
              methods: s.methods.map((m) =>
                m.type === methodType ? { ...m, ...updates } : m,
              ),
            };
          }),
        };
      }),
    );
  };

  const runAutomated = (controlId: string, stmtId: string) => {
    updateMethod(controlId, stmtId, "automated", { autoStatus: "running" });
    // Simulate automated check
    setTimeout(() => {
      const passed = Math.random() > 0.3;
      updateMethod(controlId, stmtId, "automated", {
        autoStatus: passed ? "passed" : "failed",
        result: passed ? "yes" : "no",
        reason: passed
          ? "Automated validation passed all checks."
          : "Automated validation detected non-compliant configuration.",
      });
    }, 2500);
  };

  const canCompleteControl = (ctrl: ExecutionControl) => {
    return ctrl.statements.every((s) => {
      // Statement saved via declarative modal or API result exists
      if (savedStatements.has(`${ctrl.id}:${s.id}`)) return true;
      if (declResults.some((r) => r.execution?.statementId === s.label))
        return true;

      // Check declarative+evidence inline form
      const hasDeEvMethod = s.assessmentMethods.some(
        (m) => m === "declarative_evidence",
      );
      if (hasDeEvMethod) {
        const form = declEvForms[s.id];
        if (form && form.determination !== null && form.evidenceIds.length > 0)
          return true;
      }

      // Statement already reviewed (accepted/approved, pending) or response sent (follow_up_resolved)
      const exec = executions.find((e) => e.statementId === s.label);
      if (exec) {
        const status = exec.reviewStatus;
        if (status && status !== "follow_up_raised") return true;
        // Follow-up raised + editable: check if response is saved locally
        if (status === "follow_up_raised") {
          const fu = followUps.find((f) => f.statementExecutionId === exec.id);
          if (fu && savedFollowUps[fu.id]) return true;
        }
      }
      return false;
    });
  };

  const [submittingControlId, setSubmittingControlId] = useState<string | null>(
    null,
  );
  const [savingModal, setSavingModal] = useState(false);
  const [submittingAll, setSubmittingAll] = useState(false);
  /** Track which statements have been saved (ctrlId:stmtId) */
  const [savedStatements, setSavedStatements] = useState<Set<string>>(
    new Set(),
  );

  /** Save statement execution - handles all 5 scenarios from the API spec:
   *
   * Scenario A: Automated, first save (mode=AUTOMATED, state=NEW, needs_evidence=false)
   *   → Empty body {}
   *
   * Scenario B: Manual, first save, no file (mode=MANUAL, state=NEW, needs_evidence=true)
   *   → {"execution_method": "declarative", "justification": "..."}
   *
   * Scenario C: Manual, first save, with file (mode=MANUAL, state=NEW, needs_evidence=true)
   *   → Pre-upload file → Link to statement → {"execution_method": "declarative_evidence", "justification": "..."}
   *
   * Scenario D: Followup re-save, automated (state=FOLLOWUP, needs_evidence=false)
   *   → Empty body {} (state moves to FOLLOWUP_RESOLVED, iteration bumps)
   *
   * Scenario E: Followup re-save, with evidence (state=FOLLOWUP, needs_evidence=true)
   *   → Pre-upload file → Link to statement → {} or {"execution_method": "declarative_evidence", "justification": "..."}
   */
  const handleSaveModal = async () => {
    if (!id || !modalCtrlId || !modalStmtId) return;
    const ctrl = controlsState.find((c) => c.id === modalCtrlId);
    const stmt = ctrl?.statements?.find((s) => s.id === modalStmtId);
    if (!ctrl || !stmt) return;

    setSavingModal(true);
    try {
      let payload: StatementExecutionPayload | undefined;

      // Get the statement's current state from execution-view
      // This is available in the loaded control statuses
      const controlStatus = controlStatuses.find(
        (cs) => cs.planControlId === modalCtrlId,
      );
      const stmtStatus = controlStatus?.statements?.find(
        (s) => s.id === modalStmtId,
      );
      const executionMode =
        controlStatus?.executionMode?.toUpperCase() || "MANUAL";
      const needsEvidence =
        controlStatus?.statements?.[0]?.assessmentMethods?.includes(
          "declarative_evidence",
        ) ?? false;

      // Determine scenario based on mode and needs_evidence
      if (executionMode === "AUTOMATED") {
        // Scenario A or D: Automated mode → empty body (OPA snapshot)
        // Backend defaults to execution_method="opa"
        payload = undefined; // empty body
      } else if (executionMode === "MANUAL") {
        // Scenario B or C: Manual mode
        if (modalTab === "declarative") {
          // Declarative only (no file)
          const declMethod = stmt.methods.find((m) => m.type === "declarative");
          if (!declMethod || !declMethod.reason?.trim()) {
            useToastStore
              .getState()
              .showError(
                "Validation Error",
                "Justification is required for declarative save",
              );
            setSavingModal(false);
            return;
          }
          payload = {
            execution_method: "declarative",
            justification: declMethod.reason,
          };
        } else if (modalTab === "declarative_evidence") {
          // Declarative + Evidence (with file)
          const form = getDeclEvForm(modalStmtId);
          if (!form.justification?.trim()) {
            useToastStore
              .getState()
              .showError(
                "Validation Error",
                "Justification is required for declarative_evidence save",
              );
            setSavingModal(false);
            return;
          }
          if (form.evidenceIds.length === 0) {
            useToastStore
              .getState()
              .showError(
                "Validation Error",
                "Evidence is required for declarative_evidence save",
              );
            setSavingModal(false);
            return;
          }
          payload = {
            execution_method: "declarative_evidence",
            justification: form.justification,
          };
        }
      }

      // Call the statement execution endpoint
      // The API URL: POST /api/v1/assessment-plans/{planId}/statements/{statementId}/execution
      // statementId is the UUID (modalStmtId), not the text label
      const response = await executeStatement(id, modalStmtId, payload);

      // Handle successful response
      if (response?.data) {
        const execResp = response.data;
        // Update local state to mark statement as saved
        setSavedStatements((prev) =>
          new Set(prev).add(`${modalCtrlId}:${modalStmtId}`),
        );

        // Show success message with the new state
        const stateMessage =
          execResp.state === "FOLLOWUP_RESOLVED"
            ? "Followup resolved"
            : "Awaiting review";
        useToastStore
          .getState()
          .showSuccess(
            "Statement Saved",
            `${stateMessage} (iteration ${execResp.iteration})`,
          );

        // Optimistically update executionMethod in controlsState so the
        // Test Method column reflects the new value immediately without
        // waiting for the server round-trip.
        if (payload?.execution_method) {
          setControlsState((prev) =>
            prev.map((ctrl) =>
              ctrl.id !== modalCtrlId
                ? ctrl
                : {
                    ...ctrl,
                    statements: ctrl.statements.map((s) =>
                      s.id !== modalStmtId
                        ? s
                        : {
                            ...s,
                            executionMethod: payload.execution_method as
                              | "declarative"
                              | "declarative_evidence"
                              | "opa",
                          },
                    ),
                  },
            ),
          );
        }

        // Refresh both APIs to sync UI with server state.
        // refreshExecutionView() updates controlsState (state field drives
        // the status badge + Action column); fetchReviewStatus() updates
        // executions/followUps/declResults. Without refreshExecutionView the
        // declarative/D+E row's status sticks at "Not Assessed" until a
        // hard page refresh.
        await Promise.all([refreshExecutionView(), fetchReviewStatus()]);
        closeModal();
      }
    } catch (err: any) {
      // Parse API error messages according to the spec
      let errorMsg = "Failed to save statement. Please try again.";

      if (err?.message) {
        errorMsg = err.message;
      } else if (err?.responseBody?.errors?.[0]) {
        errorMsg =
          err.responseBody.errors[0].detail ||
          err.responseBody.errors[0].title ||
          errorMsg;
      }

      // Handle specific error cases from the spec
      if (errorMsg.includes("justification is required")) {
        useToastStore
          .getState()
          .showError("Validation Error", "Justification is required");
      } else if (errorMsg.includes("evidence")) {
        useToastStore.getState().showError("Validation Error", errorMsg);
      } else if (errorMsg.includes("already pending review")) {
        useToastStore
          .getState()
          .showError(
            "Cannot Save",
            "This statement is already pending review. Cannot re-save.",
          );
      } else if (errorMsg.includes("locked") || errorMsg.includes("REVIEWED")) {
        useToastStore
          .getState()
          .showError(
            "Locked",
            "This statement has been reviewed and is locked.",
          );
      } else {
        useToastStore.getState().showError("Error", errorMsg);
      }

      console.error("Save statement failed:", err);
    }
    setSavingModal(false);
  };

  /** Re-save an automated control that the reviewer put back in FOLLOWUP state.
   * Case 1 (needs_evidence false): re-confirm only → POST execution with empty body.
   * Case 2 (needs_evidence true): upload + link the file, then POST execution empty body. */
  const handleAutoFollowUpResave = async (
    statementId: string,
    statementLabel: string,
    needsEvidence: boolean,
    justification?: string,
  ) => {
    if (!id) return;
    if (needsEvidence && !autoFuFile) {
      useToastStore
        .getState()
        .showError("Evidence Required", "Attach a file before re-saving.");
      return;
    }
    setAutoFuSaving(true);
    try {
      if (needsEvidence && autoFuFile) {
        const createRes = await createSupportingEvidence({
          title: autoFuFile.name,
          tags: [],
          owningOrgUnitId: owningOrgUnitId || undefined,
        });
        await uploadEvidenceFile(createRes.data.id, autoFuFile);
        await linkEvidenceToStatement(id, {
          statementId: statementLabel,
          evidenceIds: [createRes.data.id],
        });
      }
      // Backend keeps the OPA cells (+ linked file) and resolves the follow-up.
      // Justification is optional for automated controls; send it when provided.
      const trimmedJustification = justification?.trim();
      await executeStatement(
        id,
        statementId,
        trimmedJustification
          ? { justification: trimmedJustification }
          : undefined,
      );
      await refreshExecutionView();
      await fetchReviewStatus();
      setAutoFuFile(null);
      setAutoFuJustification("");
      setAutoRespondOpen(false);
      setAutoModalOpen(false);
      useToastStore
        .getState()
        .showSuccess("Follow-Up Resolved", "Re-saved for review.");
    } catch (err: any) {
      const apiMsg =
        err?.responseBody?.errors?.[0]?.detail ||
        err?.responseBody?.errors?.[0]?.title ||
        err?.message;
      useToastStore
        .getState()
        .showError(
          "Re-Save Failed",
          apiMsg || "Failed to re-save this control.",
        );
      console.error("Auto follow-up re-save failed:", err);
    }
    setAutoFuSaving(false);
  };

  /** Re-fetch the execution view and update the control/state tables. */
  const refreshExecutionView = async () => {
    if (!id) return;
    try {
      const res = await getAssessmentExecutionView(id);
      const statuses = mapExecutionViewToStatuses(res.data);
      setControlStatuses(statuses);
      setControlsState(mapStatusesToControls(statuses));
      // Keep the open automation modal in sync with refreshed data.
      setAutoModalCtrl((prev) =>
        prev
          ? (statuses.find((s) => s.planControlId === prev.planControlId) ??
            prev)
          : prev,
      );
    } catch {
      /* ignore */
    }
  };

  /** Statements of an automated control that can be saved right now.
   *  Rule (from BE spec): mode === AUTOMATED && state ∈ {NEW, FOLLOWUP}
   *  && assets non-empty (there's actual ELK data to freeze). */
  const getSavableAutoStatements = (
    ctrl: ControlExecutionStatusResponse | null,
  ): AssessmentExecutionViewStatement[] => {
    if (!ctrl) return [];
    if ((ctrl.executionMode ?? "").toUpperCase() !== "AUTOMATED") return [];
    return (ctrl.executionViewStatements ?? []).filter((s) => {
      const st = (s.state ?? "NEW").toUpperCase();
      const savableState = st === "NEW" || st === "FOLLOWUP";
      return savableState && (s.assets?.length ?? 0) > 0;
    });
  };

  /** Save an automated control's NEW statements — empty body {} per statement,
   *  backend snapshots the live OPA cells and moves state → AWAITING_REVIEW.
   *  (FOLLOWUP re-save is handled separately by handleAutoFollowUpResave.) */
  const [savingAutoModal, setSavingAutoModal] = useState(false);
  const handleSaveAutomated = async () => {
    if (!id || !autoModalCtrl) return;
    const savable = getSavableAutoStatements(autoModalCtrl).filter(
      (s) => (s.state ?? "NEW").toUpperCase() === "NEW",
    );
    if (savable.length === 0) return;

    setSavingAutoModal(true);
    try {
      await Promise.all(
        savable.map((s) => executeStatement(id, s.statement_id)),
      );
      useToastStore
        .getState()
        .showSuccess(
          "Saved",
          `Automated results submitted (${savable.length} statement${savable.length !== 1 ? "s" : ""})`,
        );
      await Promise.all([refreshExecutionView(), fetchReviewStatus()]);
      setAutoModalOpen(false);
    } catch (err: any) {
      const apiMsg: string = err?.message || "";
      let msg = apiMsg || "Failed to save automated results";
      if (/not found|404/i.test(apiMsg)) {
        msg = "Plan or statement not found.";
      } else if (/evidence/i.test(apiMsg)) {
        msg = "The reviewer requested evidence — attach a file before saving.";
      } else if (/already|pending review|locked|reviewed/i.test(apiMsg)) {
        msg = "This statement is already submitted and can't be re-saved.";
      }
      useToastStore.getState().showError("Error", msg);
    }
    setSavingAutoModal(false);
  };

  /* ── Declarative results from API (previously saved) ── */
  const [declResults, setDeclResults] = useState<ExecutionReviewItem[]>([]);

  /* ── Review status state ── */
  const [executions, setExecutions] = useState<ExecutionResponse[]>([]);
  const [reviewStatusLoaded, setReviewStatusLoaded] = useState(false);
  const [followUps, setFollowUps] = useState<FollowUpItem[]>([]);
  const [reviewItems, setReviewItems] = useState<ExecutionReviewItem[]>([]);

  /* Check if any follow-up was responded to but not yet accepted by assessor */
  const hasPendingFollowUpResponse = reviewItems.some(
    (ri) =>
      ri.reviewHistory?.some(
        (h) => h.reviewStatus === "follow_up_resolved" && h.followUpResponse,
      ) &&
      ri.currentReviewStatus !== "accepted" &&
      ri.currentReviewStatus !== "approved",
  );

  /** Resolve the effective reviewer decision for a review item.
   *  Individual saves land as "draft" — the real decision is in reviewHistory.
   *  Only after finalizeReviews does currentReviewStatus become "accepted"/"rejected". */
  const getEffectiveReviewStatus = (
    ri: ExecutionReviewItem | undefined,
  ): string | null => {
    if (!ri) return null;
    const crs = ri.currentReviewStatus;
    if (
      crs === "accepted" ||
      crs === "approved" ||
      crs === "rejected" ||
      crs === "follow_up_raised" ||
      crs === "follow_up_resolved"
    )
      return crs;
    if (crs === "draft" && ri.reviewHistory?.length) {
      const last = [...ri.reviewHistory]
        .reverse()
        .find(
          (h) =>
            h.reviewStatus === "accepted" ||
            h.reviewStatus === "approved" ||
            h.reviewStatus === "rejected" ||
            h.reviewStatus === "follow_up_raised",
        );
      if (last) return last.reviewStatus;
    }
    return crs ?? null;
  };

  // Overlay is fully driven by the server.
  // GET /assessment-plans/{planId}/review-readiness → `allReviewed` is the
  // single source of truth for "All Controls Completed".
  const allAccepted = allReviewed;
  const allFullyAccepted = allReviewed;
  const [followUpResponses, setFollowUpResponses] = useState<
    Record<string, string>
  >({});
  /** Locally saved follow-up responses: followUpId → response text */
  const [savedFollowUps, setSavedFollowUps] = useState<Record<string, string>>(
    {},
  );
  /** Follow-up evidence: followUpId → list of selected evidence */
  const [followUpEvidenceIds, setFollowUpEvidenceIds] = useState<
    Record<string, { id: string; name: string }[]>
  >({});
  const [fuPickerOpen, setFuPickerOpen] = useState(false);
  const [fuPickerReviewId, setFuPickerReviewId] = useState<string | null>(null);
  const [fuPickerStatementId, setFuPickerStatementId] = useState<string | null>(
    null,
  );
  const [fuAvailableEvidence, setFuAvailableEvidence] = useState<
    SupportingEvidence[]
  >([]);
  const [fuLinkedEvidenceIds, setFuLinkedEvidenceIds] = useState<Set<string>>(
    new Set(),
  );
  const [fuSearchQuery, setFuSearchQuery] = useState("");
  const [fuPickerLoading, setFuPickerLoading] = useState(false);
  const [fuSelectedIds, setFuSelectedIds] = useState<Set<string>>(new Set());
  const [fuUploading, setFuUploading] = useState(false);
  /** Files uploaded during the current follow-up picker session — shown
   *  inline inside the upload card instead of the checkbox list. */
  const [fuJustUploaded, setFuJustUploaded] = useState<SupportingEvidence[]>(
    [],
  );
  /** Current page in the follow-up evidence picker list (10 per page). */
  const [fuListPage, setFuListPage] = useState(0);

  const visibleTotalPages = totalPages;
  const visiblePagedControls = pagedControls;

  /* ── Chat modal state ── */
  const [chatOpen, setChatOpen] = useState(false);
  const [chatStmtLabel, setChatStmtLabel] = useState("");
  const [chatControlId, setChatControlId] = useState("");
  const [chatExecId, setChatExecId] = useState("");
  const [chatSource, setChatSource] = useState<
    "assess" | "auto" | "followup" | "view" | null
  >(null);
  /** Remembers the View-modal target so the chat's back/close can restore it. */
  const chatReturnViewRef = useRef<{
    stmt: ExecutionStatement;
    ctrl: ExecutionControl | null;
  } | null>(null);

  /** Open the chat from the read-only View modal: close the View modal (it sits
   *  above the chat z-index), then restore it on back/close via chatSource "view". */
  const openChatFromView = (
    stmtLabel: string,
    controlId: string,
    execId: string,
  ) => {
    chatReturnViewRef.current = { stmt: viewModalStmt!, ctrl: viewModalCtrl };
    setChatStmtLabel(stmtLabel);
    setChatControlId(controlId);
    setChatExecId(execId);
    setChatSource("view");
    setViewModalStmt(null);
    setViewModalCtrl(null);
    setChatOpen(true);
  };

  const openChat = (stmtLabel: string, controlId: string, execId: string) => {
    setChatStmtLabel(stmtLabel);
    setChatControlId(controlId);
    setChatExecId(execId);
    // Hide the source modal when chat opens
    if (assessModalOpen) {
      setChatSource("assess");
      setAssessModalOpen(false);
    } else if (autoModalOpen) {
      setChatSource("auto");
      setAutoModalOpen(false);
    } else {
      setChatSource(null);
    }
    setChatOpen(true);
  };

  /* ── Statement-level counts ── */
  const totalStatements = controlsState.reduce(
    (sum, c) => sum + c.statements.length,
    0,
  );
  const completedStatements = controlsState.reduce(
    (sum, c) =>
      sum +
      c.statements.filter((s) => {
        if (savedStatements.has(`${c.id}:${s.id}`)) return true;
        if (declResults.some((r) => r.execution?.statementId === s.label))
          return true;
        const exec = executions.find((e) => e.statementId === s.label);
        return !!exec;
      }).length,
    0,
  );
  const stmtProgressPct =
    totalStatements > 0 ? (completedStatements / totalStatements) * 100 : 0;

  /* ── Compliance score counts (for header bar) ── */
  /* Compliance score code intentionally disabled. */
  const satisfiedCount = 0;
  const notSatisfiedCount = 0;
  const totalItems = 0;
  const notCompletedCount = 0;
  const compliancePct = 0;
  const compliancePctColor = "text-[#94a3b8] dark:text-white/60";

  /* ── Tree helpers ── */
  const getStmtStatus = (
    ctrl: ExecutionControl,
    stmt: ExecutionStatement,
  ): "active" | "completed" | "follow_up" | "pending" => {
    if (modalCtrlId === ctrl.id && modalStmtId === stmt.id) return "active";
    const exec = executions.find((e) => e.statementId === stmt.label);
    const ri = reviewItems.find((r) => r.execution?.statementId === stmt.label);
    const ers = getEffectiveReviewStatus(ri);
    if (exec?.reviewStatus === "follow_up_raised" || ers === "follow_up_raised")
      return "follow_up";
    if (
      ers === "accepted" ||
      ers === "approved" ||
      exec?.reviewStatus === "accepted" ||
      exec?.reviewStatus === "approved"
    )
      return "completed";
    if (ers === "rejected" || exec?.reviewStatus === "rejected")
      return "completed";
    if (exec?.reviewStatus === "pending") return "completed";
    if (savedStatements.has(`${ctrl.id}:${stmt.id}`)) return "completed";
    if (declResults.some((r) => r.execution?.statementId === stmt.label))
      return "completed";
    if (exec) return "completed";
    return "pending";
  };

  const filteredTreeControls = controlsState.filter((ctrl) => {
    const q = treeSearch.toLowerCase();
    const matchesSearch =
      !q ||
      ctrl.controlId.toLowerCase().includes(q) ||
      ctrl.description.toLowerCase().includes(q) ||
      ctrl.statements.some(
        (s) =>
          s.label.toLowerCase().includes(q) || s.text.toLowerCase().includes(q),
      );
    if (!matchesSearch) return false;
    if (treeFilter === "all") return true;
    return ctrl.statements.some((s) => {
      const status = getStmtStatus(ctrl, s);
      if (treeFilter === "pending")
        return status === "pending" || status === "active";
      if (treeFilter === "completed") return status === "completed";
      if (treeFilter === "follow_up") return status === "follow_up";
      return true;
    });
  });

  /** Select a statement in the tree (replaces modal open) */
  const selectStatement = (ctrlId: string, stmtId: string) => {
    setModalCtrlId(ctrlId);
    setModalStmtId(stmtId);
    setModalTab("declarative");
    setDeEvStep(1);
    setDeEvSelectedFiles([]);
    setAttachSelectedIds(new Set());
    setEvidenceSearch("");
    setExpandedTreeIds((prev) => new Set(prev).add(ctrlId));
  };

  /** Advance to the next pending statement */
  const advanceToNextStatement = () => {
    if (!modalCtrlId || !modalStmtId) return;
    let found = false;
    let passedCurrent = false;
    for (const ctrl of controlsState) {
      for (const stmt of ctrl.statements) {
        if (ctrl.id === modalCtrlId && stmt.id === modalStmtId) {
          passedCurrent = true;
          continue;
        }
        if (passedCurrent && getStmtStatus(ctrl, stmt) === "pending") {
          selectStatement(ctrl.id, stmt.id);
          found = true;
          return;
        }
      }
    }
    if (!found) {
      for (const ctrl of controlsState) {
        for (const stmt of ctrl.statements) {
          if (ctrl.id === modalCtrlId && stmt.id === modalStmtId) return;
          if (getStmtStatus(ctrl, stmt) === "pending") {
            selectStatement(ctrl.id, stmt.id);
            return;
          }
        }
      }
    }
  };

  /** Fetch executions + follow-ups + review items to show review status */
  const fetchReviewStatus = async () => {
    if (!id) return;
    let loadedExecs: ExecutionResponse[] = [];
    let loadedDeclResults: ExecutionReviewItem[] = [];
    try {
      const [execRes, fuRes, declRes] = await Promise.all([
        getExecutions(id).catch(() => ({ data: [] as ExecutionResponse[] })),
        getFollowUps(id).catch(() => ({ data: [] as FollowUpItem[] })),
        getDeclarativeResults(id, "assessment_execution").catch(() => ({
          data: [] as ExecutionReviewItem[],
        })),
      ]);
      loadedExecs = execRes.data ?? [];
      loadedDeclResults = declRes.data ?? [];
      setExecutions(loadedExecs);
      setFollowUps(fuRes.data ?? []);
      setDeclResults(loadedDeclResults);
    } catch {
      /* ignore */
    }
    setReviewStatusLoaded(true);

    // Derive completed state and pre-fill form fields from draft data
    setControlsState((prev) =>
      prev.map((ctrl) => {
        const updatedStatements = ctrl.statements.map((s) => {
          // Check for existing execution (draft or submitted)
          const exec = loadedExecs.find((e) => e.statementId === s.label);
          if (exec && exec.editable) {
            // Draft execution — pre-fill form fields so user can continue editing
            const updatedMethods = s.methods.map((m) => {
              if (m.type === "declarative") {
                return {
                  ...m,
                  result: (exec.determination ? "yes" : "no") as MethodResult,
                  reason: exec.justification ?? "",
                };
              }
              return m;
            });
            return { ...s, methods: updatedMethods };
          }
          return s;
        });

        const allDone = updatedStatements.every((s) => {
          const exec = loadedExecs.find((e) => e.statementId === s.label);
          if (exec) return true;
          if (
            loadedDeclResults.some((r) => r.execution?.statementId === s.label)
          )
            return true;
          return false;
        });
        return {
          ...ctrl,
          statements: updatedStatements,
          completed: allDone || ctrl.completed,
        };
      }),
    );
  };

  // Load evidence list when attach modal opens or when step 2 existing sub-tab is active
  useEffect(() => {
    if (attachModalOpen) {
      searchEvidence("");
    }
  }, [attachModalOpen]);

  useEffect(() => {
    if (assessModalOpen) {
      setDeEvSelectedExisting(new Set());
      setDeEvSelectedFiles([]);
      setDeEvExistingSearch("");
      searchEvidence("");
    }
  }, [assessModalOpen]);

  /** Get review status for a statement */
  const getStatementReviewStatus = (stmtLabel: string) => {
    return executions.find((e) => e.statementId === stmtLabel) ?? null;
  };

  /** Get follow-up for a statement execution */
  const getStatementFollowUp = (executionId: string) => {
    return (
      followUps.find((f) => f.statementExecutionId === executionId) ?? null
    );
  };

  /** After a follow-up is resolved the backend drops it from /follow-ups and
   *  resets reviewStatus back to "pending". Recover the original question +
   *  submitted response from review history for display. */
  const getResolvedFollowUp = (
    executionId: string,
  ): { question: string | null; response: string | null } | null => {
    const ri = reviewItems.find((r) => r.execution?.id === executionId);
    if (!ri?.reviewHistory?.length) return null;
    const resolved = [...ri.reviewHistory]
      .reverse()
      .find(
        (h) => h.reviewStatus === "follow_up_resolved" && h.followUpResponse,
      );
    if (!resolved) return null;
    // The question lives on the immediately-preceding follow_up_raised entry
    const idx = ri.reviewHistory.indexOf(resolved);
    const raised = [...ri.reviewHistory.slice(0, idx)]
      .reverse()
      .find((h) => h.reviewStatus === "follow_up_raised" && h.followUpQuestion);
    return {
      question: raised?.followUpQuestion ?? resolved.followUpQuestion ?? null,
      response: resolved.followUpResponse,
    };
  };

  /** Save follow-up response locally (API call happens on Move to Review) */
  const handleFollowUpResponse = (reviewId: string) => {
    const response = followUpResponses[reviewId]?.trim();
    if (!response) return;
    setSavedFollowUps((prev) => ({ ...prev, [reviewId]: response }));
    setFollowUpResponses((prev) => ({ ...prev, [reviewId]: "" }));
  };

  /** Open evidence picker for follow-up response */
  const openEvidencePicker = async (reviewId: string, statementId: string) => {
    setFuPickerReviewId(reviewId);
    setFuPickerStatementId(statementId);
    setFuSearchQuery("");
    setFuPickerOpen(true);
    setFuPickerLoading(true);
    setFuJustUploaded([]);
    setFuListPage(0);

    // Pre-select any evidence already picked for this follow-up
    const alreadyPicked = followUpEvidenceIds[reviewId]?.map((e) => e.id) ?? [];
    setFuSelectedIds(new Set(alreadyPicked));

    try {
      const [allEvRes, linkedRes] = await Promise.all([
        listSupportingEvidence(),
        id
          ? getEvidenceByStatement(id, statementId)
          : Promise.resolve({ data: [] as EvidenceByStatementItem[] }),
      ]);
      setFuAvailableEvidence(allEvRes.data ?? []);
      setFuLinkedEvidenceIds(
        new Set((linkedRes.data ?? []).map((e) => e.supportingEvidenceId)),
      );
    } catch (err) {
      console.error("Failed to load evidence for picker:", err);
    }
    setFuPickerLoading(false);
  };

  /** Confirm evidence picker selection */
  const confirmEvidencePicker = () => {
    if (!fuPickerReviewId) return;
    const fromList = fuAvailableEvidence
      .filter((e) => fuSelectedIds.has(e.id) && !fuLinkedEvidenceIds.has(e.id))
      .map((e) => ({ id: e.id, name: e.title || e.fileName || e.id }));
    const fromUploads = fuJustUploaded.map((e) => ({
      id: e.id,
      name: e.title || e.fileName || e.id,
    }));
    // Merge, dedup by id
    const merged: { id: string; name: string }[] = [];
    const seen = new Set<string>();
    for (const item of [...fromUploads, ...fromList]) {
      if (seen.has(item.id)) continue;
      seen.add(item.id);
      merged.push(item);
    }
    setFollowUpEvidenceIds((prev) => ({
      ...prev,
      [fuPickerReviewId!]: merged,
    }));
    setFuJustUploaded([]);
    setFuPickerOpen(false);
  };

  /** Upload new evidence from follow-up picker — file is displayed inline
   *  inside the upload card and only committed on Attach Selected. */
  const handleFuUploadEvidence = async (file: File) => {
    if (!fuPickerReviewId) return;
    setFuUploading(true);
    try {
      const title = file.name;
      const createRes = await createSupportingEvidence({
        title,
        tags: [],
        owningOrgUnitId: owningOrgUnitId || undefined,
      });
      const evidence = createRes.data;
      const uploadRes = await uploadEvidenceFile(evidence.id, file);
      const uploaded = uploadRes.data;
      setEvidenceCache((prev) => ({ ...prev, [uploaded.id]: uploaded }));
      setFuJustUploaded((prev) => [uploaded, ...prev]);
    } catch (err) {
      console.error("Follow-up evidence upload failed:", err);
    }
    setFuUploading(false);
  };

  /** Mark control as complete locally (no API call) */
  const markControlComplete = (controlId: string) => {
    setControlsState((prev) =>
      prev.map((c) => (c.id === controlId ? { ...c, completed: true } : c)),
    );
    setExpandedId(null);
  };

  /** Move to Review — submit all completed controls via API */
  const handleMoveToReview = async () => {
    if (!id) return;

    setSubmittingAll(true);
    try {
      // 1. Submit any locally saved follow-up responses first (these aren't auto-saved as drafts)
      const followUpEntries = Object.entries(savedFollowUps);
      for (const [followUpId, responseText] of followUpEntries) {
        const evIds = followUpEvidenceIds[followUpId]?.map((e) => e.id);
        const fu = followUps.find((f) => f.id === followUpId);
        const exec = fu
          ? executions.find((e) => e.id === fu.statementExecutionId)
          : undefined;
        if (!exec) continue;
        await respondToFollowUp(
          id,
          exec.id,
          responseText,
          evIds,
          exec.determination,
          exec.justification,
        );
      }

      if (followUpEntries.length > 0) {
        setSavedFollowUps({});
        setFollowUpEvidenceIds({});
      }

      // 2. Finalize execution — auto-create execution records from OPA results
      try {
        await finalizeExecution(id);
      } catch (e) {
        console.error("finalizeExecution failed:", e);
      }

      // 3. Submit all for review — transitions all drafts to pending
      try {
        await submitForReview(id);
      } catch (e) {
        console.error("submitForReview failed:", e);
      }

      try {
        await fetchReviewStatus();
      } catch (e) {
        console.error("fetchReviewStatus failed:", e);
      }
      try {
        await refreshReviewReadiness();
      } catch (e) {
        console.error("refreshReviewReadiness failed:", e);
      }
      useToastStore
        .getState()
        .showSuccess(
          "Successfully Moved to Review",
          "All completed controls have been submitted and are now awaiting assessor review.",
        );
    } catch (err: any) {
      console.error("Failed to submit for review:", err);
      const apiMsg =
        err?.responseBody?.errors?.[0]?.detail ||
        err?.responseBody?.errors?.[0]?.title ||
        err?.detail ||
        err?.message;
      useToastStore
        .getState()
        .showError(
          "Error",
          apiMsg || "Failed to submit for review. Please try again.",
        );
    } finally {
      setSubmittingAll(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center gap-3 py-24">
        <Loader2 className="size-5 animate-spin text-indigo-500 dark:text-indigo-400" />
        <span className="text-[13px] text-muted-foreground">Loading</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-24">
        <p className="text-[14px] text-[#94a3b8] dark:text-white/70">
          Something went wrong.
        </p>
        <button
          onClick={() => {
            setError(null);
            window.location.reload();
          }}
          className="px-4 py-2 rounded-lg border border-border text-sm hover:bg-accent"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <PageShell className="space-y-6">
      <Breadcrumb
        parent="Assessment Execution"
        parentTo="/continuous-compliance/assessment/assessment-execution"
        current={planName || "Assessment Execution"}
      />

      {/* ── Plan Information ── */}
      {planInfo &&
        (() => {
          const sspTitle = planInfo.sspTitle || planInfo.importSspHref || "—";
          const startDate = planInfo.task?.startDate
            ? new Date(planInfo.task.startDate).toLocaleDateString("en-GB")
            : "—";
          const endDate = planInfo.task?.endDate
            ? new Date(planInfo.task.endDate).toLocaleDateString("en-GB")
            : "—";
          const cards = [
            {
              label: "Plan Name",
              value: planInfo.title ?? "—",
              icon: ClipboardList,
              color: "text-indigo-500 dark:text-indigo-400",
              bg: "bg-indigo-50 dark:bg-indigo-500/10",
            },
            {
              label: "Linked SSP",
              value: sspTitle,
              icon: Link2,
              color: "text-sky-500 dark:text-sky-400",
              bg: "bg-sky-50 dark:bg-sky-500/10",
            },
            {
              label: "Start Date",
              value: startDate,
              icon: Calendar,
              color: "text-emerald-500 dark:text-emerald-400",
              bg: "bg-emerald-50 dark:bg-emerald-500/10",
            },
            {
              label: "End Date",
              value: endDate,
              icon: CalendarCheck,
              color: "text-amber-500 dark:text-amber-400",
              bg: "bg-amber-50 dark:bg-amber-500/10",
            },
          ];
          return (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-4">
                {cards.map((c) => {
                  const Icon = c.icon;
                  return (
                    <div
                      key={c.label}
                      className="rounded-xl border border-[#e2e8f0] dark:border-white/[0.06] bg-white dark:bg-[#131320] px-4 py-3 flex items-center gap-3 min-w-0"
                    >
                      <div
                        className={`size-9 rounded-lg flex items-center justify-center flex-shrink-0 ${c.bg}`}
                      >
                        <Icon className={`size-4 ${c.color}`} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-[11px] font-medium text-[#94a3b8] dark:text-white/60">
                          {c.label}
                        </p>
                        <p
                          className="text-[13px] font-semibold text-[#0f172a] dark:text-white truncate"
                          title={String(c.value)}
                        >
                          {c.value}
                        </p>
                      </div>
                    </div>
                  );
                })}
            </div>
          );
        })()}

      {/* ── Statements DataTable ── */}
      <div
        className={`relative flex flex-col flex-1 min-h-0 mt-8 gap-4 ${allAccepted ? "overflow-hidden" : ""}`}>
        {/* ── All Controls Completed Overlay ── */}
        {allAccepted && (
          <div className="absolute inset-0 z-20 flex items-center justify-center bg-black/40 backdrop-blur-sm">
            <div className="flex flex-col items-center justify-center gap-3 px-8 py-10 rounded-2xl bg-white/90 dark:bg-[#131320]/90 shadow-xl border border-emerald-200 dark:border-emerald-500/20">
              <div className="size-14 rounded-full bg-emerald-100 dark:bg-emerald-500/15 flex items-center justify-center">
                <CheckCircle2 className="size-7 text-emerald-600 dark:text-emerald-400" />
              </div>
              <h3 className="text-lg font-bold text-emerald-800 dark:text-emerald-300">
                All Controls Completed
              </h3>
              <p className="text-sm text-emerald-700 dark:text-emerald-400/80 text-center max-w-md">
                {allFullyAccepted
                  ? "All controls have been reviewed and accepted. You can now view the assessment results."
                  : "All controls have been reviewed. You can now view the assessment results."}
              </p>
              <button
                onClick={() => navigate(`/continuous-compliance/assessment/assessment-result/${id}`)}
                className="px-6 py-2.5 rounded-lg text-sm font-bold bg-emerald-600 text-white hover:bg-emerald-700 transition-all shadow-sm"
              >
                View Results
              </button>
            </div>
          </div>
        )}
        {(() => {
          let idx = 0;
          const autoControls = controlStatuses.filter(
            (s) =>
              s.controlType === "automated" && (s.opaResults ?? []).length > 0,
          );
          const autoControlIds = new Set(
            autoControls.map((ac) => ac.controlId),
          );
          const declRows: TableRow[] = controlsState.flatMap((ctrl) =>
            ctrl.statements
              .filter(() => !autoControlIds.has(ctrl.controlId))
              .map((stmt) => ({
                ctrl,
                stmt,
                idx: idx++,
                kind: "declarative" as const,
              })),
          );
          const autoRows: TableRow[] = autoControls.map((ac) => ({
            autoCtrl: ac,
            idx: idx++,
            kind: "automation" as const,
          }));
          const rows: TableRow[] = [...declRows, ...autoRows];

          const getCtrlId = (row: TableRow) =>
            row.kind === "declarative"
              ? row.ctrl.controlId
              : row.autoCtrl.controlId;
          const getCtrlTitle = (row: TableRow) =>
            row.kind === "declarative"
              ? (row.ctrl.description ?? row.ctrl.controlId)
              : (row.autoCtrl.controlDescription ?? row.autoCtrl.controlId);
          const getStmtText = (row: TableRow) => {
            if (row.kind === "declarative") return row.stmt.text ?? "";
            const stmts = row.autoCtrl.statements ?? [];
            return (
              stmts
                .map((s) => s.statementDescription)
                .filter(Boolean)
                .join("; ") || ""
            );
          };

          const getRowStatus = (
            row: TableRow,
          ):
            | "pending"
            | "completed"
            | "accepted"
            | "rejected"
            | "follow_up"
            | "follow_up_responded"
            | "auto_passed"
            | "auto_failed" => {
            if (row.kind === "automation") {
              // Execution-view is the source of truth on this page (the review-status
              // APIs aren't loaded here). A statement still in FOLLOWUP means the owner
              // owes a re-save → surface the "Respond" action.
              const evStates = (row.autoCtrl.executionViewStatements ?? []).map(
                (s) => (s.state ?? "").toUpperCase(),
              );
              if (evStates.includes("FOLLOWUP")) return "follow_up";
              const autoExec = executions.find(
                (e) => e.controlId === row.autoCtrl.controlId,
              );
              const autoRi = reviewItems.find(
                (r) => r.execution?.id === autoExec?.id,
              );
              const autoErs = getEffectiveReviewStatus(autoRi);
              if (
                autoExec?.reviewStatus === "follow_up_raised" ||
                autoErs === "follow_up_raised"
              ) {
                const fu = followUps.find(
                  (f) => f.statementExecutionId === autoExec?.id,
                );
                if (fu && savedFollowUps[fu.id]) return "follow_up_responded";
                if (!fu && autoExec && savedFollowUps[autoExec.id])
                  return "follow_up_responded";
                return "follow_up";
              }
              if (
                autoRi?.reviewHistory?.some(
                  (h) =>
                    h.reviewStatus === "follow_up_resolved" &&
                    h.followUpResponse,
                )
              )
                return "completed";
              if (
                autoErs === "accepted" ||
                autoErs === "approved" ||
                autoExec?.reviewStatus === "accepted" ||
                autoExec?.reviewStatus === "approved"
              )
                return "accepted";
              if (
                autoErs === "rejected" ||
                autoExec?.reviewStatus === "rejected"
              )
                return "rejected";
              return row.autoCtrl.opaOverallStatus === "passed"
                ? "auto_passed"
                : "auto_failed";
            }
            const exec = executions.find(
              (e) => e.statementId === row.stmt.label,
            );
            const ri = reviewItems.find(
              (r) => r.execution?.statementId === row.stmt.label,
            );
            const ers = getEffectiveReviewStatus(ri);
            if (
              exec?.reviewStatus === "follow_up_raised" ||
              ers === "follow_up_raised"
            ) {
              if (exec?.editable || ers === "follow_up_raised") {
                const fu = followUps.find(
                  (f) => f.statementExecutionId === exec?.id,
                );
                if (fu && savedFollowUps[fu.id]) return "follow_up_responded";
                return "follow_up";
              }
              return "follow_up";
            }
            if (
              ers === "accepted" ||
              ers === "approved" ||
              exec?.reviewStatus === "accepted" ||
              exec?.reviewStatus === "approved"
            )
              return "accepted";
            if (ers === "rejected" || exec?.reviewStatus === "rejected")
              return "rejected";
            if (savedStatements.has(`${row.ctrl.id}:${row.stmt.id}`))
              return "completed";
            if (
              declResults.some(
                (r) => r.execution?.statementId === row.stmt.label,
              )
            )
              return "completed";
            if (exec) return "completed";
            return "pending";
          };

          /* ── Dynamic filter options from actual data ── */
          const getAvailableKinds = () => {
            const kinds = new Set<string>();
            rows.forEach(row => {
              if (row.kind === 'automation') {
                kinds.add('automation');
              } else {
                const methods = row.stmt?.assessmentMethods ?? [];
                const isDE = row.stmt?.methods?.some(m => m.type === 'declarative_evidence') ||
                            methods.includes('declarative_evidence');
                if (isDE) {
                  kinds.add('declarative_evidence');
                } else {
                  kinds.add('declarative');
                }
              }
            });
            return Array.from(kinds);
          };

          const getAvailableStatuses = () => {
            const statuses = new Set<string>();
            rows.forEach(row => {
              const rs = getRowStatus(row);
              if (rs === "pending") statuses.add('not_assessed');
              if (rs === "auto_failed" || rs === "follow_up" || rs === "rejected") statuses.add('not_satisfied');
              if (rs === "accepted") statuses.add('satisfied');
            });
            return Array.from(statuses);
          };

          const availableKinds = getAvailableKinds();
          const availableStatuses = getAvailableStatuses();

          const kindFilterOptions = [
            { value: "all", label: "All Types" },
            { value: "not_yet_assessed", label: "Not Yet Assessed" },
            { value: "declarative", label: "Declarative" },
            { value: "declarative_evidence", label: "Declarative + Evidence" },
            { value: "automation", label: "Automation" },
          ];

          const statusFilterOptions = [
            { value: "all", label: "All Status" },
            { value: "not_reviewed", label: "Not Reviewed", dot: "bg-slate-400" },
            { value: "not_assessed", label: "Not Assessed", dot: "bg-amber-500" },
            { value: "not_satisfied", label: "Not Satisfied", dot: "bg-red-500" },
            { value: "satisfied", label: "Satisfied", dot: "bg-emerald-500" },
          ];

          const filteredRows = rows.filter((row) => {
            if (!tableSearch) return true;
            const q = tableSearch.toLowerCase();
            return (
              getCtrlId(row).toLowerCase().includes(q) ||
              getCtrlTitle(row).toLowerCase().includes(q) ||
              getStmtText(row).toLowerCase().includes(q)
            );
          });

          const SortIcon = ({ col }: { col: string }) => {
            const isActive = tableSortKey === col;
            return (
              <span
                className="inline-flex flex-col items-center ml-2 select-none"
                style={{ fontSize: "7px", lineHeight: "7px" }}
              >
                <span
                  className={`font-black ${isActive && tableSortDir === "asc" ? "text-[#0f172a] dark:text-white" : "text-[#0f172a]/40 dark:text-white/40"}`}
                >
                  ▲
                </span>
                <span
                  className={`font-black ${isActive && tableSortDir === "desc" ? "text-[#0f172a] dark:text-white" : "text-[#0f172a]/40 dark:text-white/40"}`}
                >
                  ▼
                </span>
              </span>
            );
          };

          const sortedRows = [...filteredRows].sort((a, b) => {
            if (!tableSortKey) return 0;
            let aVal: any, bVal: any;
            if (tableSortKey === "idx") {
              return tableSortDir === "asc" ? a.idx - b.idx : b.idx - a.idx;
            } else if (tableSortKey === "control") {
              aVal = getCtrlId(a);
              bVal = getCtrlId(b);
            } else if (tableSortKey === "title") {
              aVal = getCtrlTitle(a);
              bVal = getCtrlTitle(b);
            } else if (tableSortKey === "assessmentMethod") {
              const methodsOf = (r: TableRow) =>
                r.kind === "automation"
                  ? (r.autoCtrl.statements ?? []).flatMap(
                      (s) => s.assessmentMethods ?? [],
                    )
                  : (r.stmt.assessmentMethods ?? []);
              const firstValid = (r: TableRow) =>
                methodsOf(r).find((m) =>
                  ["interview", "examine", "test"].includes(m),
                ) ?? "Declarative";
              aVal = firstValid(a);
              bVal = firstValid(b);
            } else if (tableSortKey === "testMethod") {
              const testMethodLabel = (r: TableRow) => {
                const execMethod =
                  r.kind === "automation"
                    ? ((r.autoCtrl.executionViewStatements ?? [])
                        .map((s) => s.execution_method)
                        .find((m) => m != null) ?? null)
                    : (r.stmt.executionMethod ?? null);
                const mode =
                  r.kind === "automation"
                    ? r.autoCtrl.executionMode
                    : controlStatuses.find(
                        (cs) => cs.planControlId === r.ctrl.id,
                      )?.executionMode;
                const effective =
                  execMethod ??
                  (mode?.toUpperCase() === "AUTOMATED" ? "opa" : null);
                const labels: Record<string, string> = {
                  opa: "Automation",
                  declarative: "Declarative",
                  declarative_evidence: "Declarative + Evidence",
                };
                return (effective && labels[effective]) || "Not Yet Assessed";
              };
              aVal = testMethodLabel(a);
              bVal = testMethodLabel(b);
            } else if (tableSortKey === "statement") {
              aVal =
                a.kind === "automation" ? getStmtText(a) : (a.stmt.text ?? "");
              bVal =
                b.kind === "automation" ? getStmtText(b) : (b.stmt.text ?? "");
            } else if (tableSortKey === "status") {
              aVal = getRowStatus(a);
              bVal = getRowStatus(b);
            } else if (tableSortKey === "action") {
              // The Action button (Assess / View / …) is derived from the row
              // status, so sort by the same status ordering.
              aVal = getRowStatus(a);
              bVal = getRowStatus(b);
            } else return 0;
            const aStr = String(aVal ?? ""), bStr = String(bVal ?? "");
            return tableSortDir === "asc"
              ? aStr.localeCompare(bStr)
              : bStr.localeCompare(aStr);
          });

          const columns: Column<TableRow>[] = [
            {
              key: "idx",
              header: "#",
              align: "center",
              render: (row) => (
                <span className="text-[12px] text-[#64748b] dark:text-white/75 font-medium">
                  {row.idx + 1}
                </span>
              ),
            },
            {
              key: "control",
              header: (
                <button
                  onClick={() => handleTableSort("control")}
                  className="flex w-full items-center justify-center hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
                >
                  Control Id
                  <SortIcon col="control" />
                </button>
              ),
              align: "center",
              render: (row) => {
                if (row.kind === "automation") {
                  return (
                    <span className="px-2 py-0.5 rounded text-[12px] font-bold bg-indigo-50 dark:bg-violet-500/10 text-indigo-700 dark:text-violet-300 border border-indigo-200 dark:border-violet-500/20">
                      {row.autoCtrl.controlId?.toUpperCase()}
                    </span>
                  );
                }
                return (
                  <span className="px-2 py-0.5 rounded text-[12px] font-bold bg-indigo-50 dark:bg-violet-500/10 text-indigo-700 dark:text-violet-300 border border-indigo-200 dark:border-violet-500/20">
                    {row.ctrl.controlId?.toUpperCase()}
                  </span>
                );
              },
            },
            {
              key: "title",
              header: (
                <button
                  onClick={() => handleTableSort("title")}
                  className="flex w-full items-center justify-center hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
                >
                  Title
                  <SortIcon col="title" />
                </button>
              ),
              align: "left",
              ellipsis: 2,
              tooltip: (row) =>
                row.kind === "automation"
                  ? row.autoCtrl.controlDescription ||
                    row.autoCtrl.controlId ||
                    ""
                  : row.ctrl.description || "",
              render: (row) => {
                if (row.kind === "automation") {
                  return (
                    <p className="text-[12px] text-[#0f172a] dark:text-white/80 leading-relaxed break-words">
                      {row.autoCtrl.controlDescription ||
                        row.autoCtrl.controlId ||
                        "—"}
                    </p>
                  );
                }
                return (
                  <p className="text-[12px] text-[#0f172a] dark:text-white/80 leading-relaxed break-words">
                    {row.ctrl.description || "—"}
                  </p>
                );
              },
            },
            {
              key: "statement",
              header: (
                <button
                  onClick={() => handleTableSort("statement")}
                  className="flex w-full items-center justify-center hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
                >
                  Statement
                  <SortIcon col="statement" />
                </button>
              ),
              align: "left",
              ellipsis: 2,
              tooltip: (row) => {
                if (row.kind === "automation") {
                  const firstStmt = row.autoCtrl.statements?.[0];
                  return firstStmt?.statementDescription || "";
                }
                return row.stmt.text || "";
              },
              render: (row) => {
                if (row.kind === "automation") {
                  const opaCount = new Set(
                    (row.autoCtrl.opaResults ?? []).map((o) => o.opaControlId),
                  ).size;
                  const firstStmt = row.autoCtrl.statements?.[0];
                  return (
                    <div className="space-y-1">
                      {firstStmt?.statementId && (
                        <span className="inline-block text-[12px] font-mono font-bold text-indigo-600 dark:text-violet-400 bg-indigo-50 dark:bg-violet-500/10 px-1.5 py-0.5 rounded">
                          {firstStmt.statementId?.toUpperCase()}
                        </span>
                      )}
                      <p className="text-[12px] text-[#475569] dark:text-white/60 leading-relaxed break-words">
                        {firstStmt?.statementDescription ||
                          `${opaCount} automated check${opaCount !== 1 ? "s" : ""}`}
                      </p>
                    </div>
                  );
                }
                return (
                  <div className="space-y-1">
                    <span className="inline-block text-[12px] font-mono font-bold text-indigo-600 dark:text-violet-400 bg-indigo-50 dark:bg-violet-500/10 px-1.5 py-0.5 rounded">
                      {row.stmt.label?.toUpperCase()}
                    </span>
                    <p className="text-[12px] text-[#475569] dark:text-white/60 leading-relaxed break-words">
                      {row.stmt.text}
                    </p>
                  </div>
                );
              },
            },
            {
              key: "assessmentMethod",
              header: (
                <button
                  onClick={() => handleTableSort("assessmentMethod")}
                  className="flex w-full items-center justify-center hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
                >
                  Assessment Method
                  <SortIcon col="assessmentMethod" />
                </button>
              ),
              align: "center",
              render: (row) => {
                const rawMethods =
                  row.kind === "automation"
                    ? Array.from(
                        new Set(
                          (row.autoCtrl.statements ?? []).flatMap(
                            (s) => s.assessmentMethods ?? [],
                          ),
                        ),
                      )
                    : (row.stmt.assessmentMethods ?? []);
                const assessMethods = rawMethods.filter((m) =>
                  ["interview", "examine", "test"].includes(m),
                );
                if (assessMethods.length === 0) {
                  return (
                    <span className="inline-flex items-center justify-center w-[100px] py-1 rounded-full text-[12px] font-bold bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-500/20">
                      Declarative
                    </span>
                  );
                }
                const colorMap: Record<string, string> = {
                  interview:
                    "bg-rose-50 dark:bg-rose-500/10 text-rose-700 dark:text-rose-400 border-rose-200 dark:border-rose-500/20",
                  examine:
                    "bg-indigo-50 dark:bg-indigo-500/10 text-indigo-700 dark:text-indigo-400 border-indigo-200 dark:border-indigo-500/20",
                  test: "bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-500/20",
                };
                return (
                  <div className="flex flex-wrap justify-center gap-1">
                    {assessMethods.map((m) => (
                      <span
                        key={m}
                        className={`inline-flex items-center justify-center w-[100px] py-1 rounded-full text-[12px] font-bold border ${colorMap[m] ?? "bg-slate-50 dark:bg-slate-500/10 text-slate-600 dark:text-slate-400 border-slate-200 dark:border-slate-500/20"}`}
                      >
                        {m.charAt(0).toUpperCase() + m.slice(1)}
                      </span>
                    ))}
                  </div>
                );
              },
            },
            {
              key: "testMethod",
              header: (
                <button
                  onClick={() => handleTableSort("testMethod")}
                  className="flex w-full items-center justify-center hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
                >
                  Test Method
                  <SortIcon col="testMethod" />
                </button>
              ),
              align: "center",
              render: (row) => {
                const execMethod =
                  row.kind === "automation"
                    ? ((row.autoCtrl.executionViewStatements ?? [])
                        .map((s) => s.execution_method)
                        .find((m) => m != null) ?? null)
                    : (row.stmt.executionMethod ?? null);
                const mode =
                  row.kind === "automation"
                    ? row.autoCtrl.executionMode
                    : controlStatuses.find(
                        (cs) => cs.planControlId === row.ctrl.id,
                      )?.executionMode;
                const effective =
                  execMethod ??
                  (mode?.toUpperCase() === "AUTOMATED" ? "opa" : null);
                const indigoColor =
                  "bg-indigo-50 dark:bg-indigo-500/10 text-indigo-700 dark:text-indigo-400 border-indigo-200 dark:border-indigo-500/20";
                const notAssessed = {
                  label: "Not Yet Assessed",
                  color: indigoColor,
                };
                const config: Record<string, { label: string; color: string }> =
                  {
                    opa: {
                      label: "Automation",
                      color: indigoColor,
                    },
                    declarative: {
                      label: "Declarative",
                      color: indigoColor,
                    },
                    declarative_evidence: {
                      label: "Declarative + Evidence",
                      color: indigoColor,
                    },
                  };
                const cfg = (effective && config[effective]) || notAssessed;
                return (
                  <span
                    className={`inline-flex items-center justify-center w-[170px] py-1.5 rounded-full text-[12px] font-bold whitespace-nowrap border ${cfg.color}`}
                  >
                    {cfg.label}
                  </span>
                );
              },
            },
            {
              key: "action",
              header: (
                <button
                  onClick={() => handleTableSort("action")}
                  className="flex w-full items-center justify-center hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
                >
                  Action
                  <SortIcon col="action" />
                </button>
              ),
              align: "center",
              headerAlign: "center",
              render: (row) => {
                const primaryAction = (() => {
                const status = getRowStatus(row);
                // A statement had a follow-up interaction if its review history
                // contains any question or response. Used to flip the View icon
                // to a chat bubble when the user has already responded.
                const rowExecId =
                  row.kind === "automation"
                    ? executions.find(
                        (e) =>
                          e.planControlId === row.autoCtrl.planControlId ||
                          e.controlId === row.autoCtrl.controlId,
                      )?.id
                    : executions.find((e) => e.statementId === row.stmt.label)
                        ?.id;
                const hasFollowUpHistory =
                  !!rowExecId &&
                  reviewItems.some(
                    (r) =>
                      r.execution?.id === rowExecId &&
                      (r.reviewHistory ?? []).some(
                        (h) => h.followUpQuestion || h.followUpResponse,
                      ),
                  );
                if (row.kind === "automation") {
                  // `review_status === "follow_up_resolved"` overrides a lagged
                  // `state === "FOLLOWUP"` — backend updates review_status first.
                  const autoResponded = (
                    row.autoCtrl.executionViewStatements ?? []
                  ).some(
                    (s) =>
                      (s.state ?? "").toUpperCase() === "FOLLOWUP_RESOLVED" ||
                      (s.review_status ?? "").toLowerCase() ===
                        "follow_up_resolved",
                  );
                  // Execution-view is the source of truth: any statement in FOLLOWUP
                  // state means the assessor wants a response from the system owner.
                  const autoFollowUp = (
                    row.autoCtrl.executionViewStatements ?? []
                  ).some((s) => (s.state ?? "").toUpperCase() === "FOLLOWUP");
                  if (
                    (status === "follow_up" || autoFollowUp) &&
                    !autoResponded
                  ) {
                    return (
                      <button
                        onClick={() => {
                          setAutoModalCtrl(row.autoCtrl);
                          setAutoOpaPage(0);
                          setAutoModalOpen(true);
                        }}
                        className="inline-flex items-center justify-center w-[110px] py-1.5 rounded-lg text-[12px] font-bold bg-amber-500 dark:bg-amber-600 text-white hover:opacity-90 transition-all"
                      >
                        Respond
                      </button>
                    );
                  }
                  if (status === "rejected") {
                    return (
                      <button
                        onClick={() => {
                          setAutoModalCtrl(row.autoCtrl);
                          setAutoOpaPage(0);
                          setAutoModalOpen(true);
                        }}
                        className="inline-flex items-center justify-center gap-1.5 w-[110px] py-1.5 rounded-lg text-[12px] font-medium bg-indigo-600 text-white hover:opacity-90 transition-all"
                      >
                        <XCircle className="size-3.5" /> Rejected
                      </button>
                    );
                  }
                  if (status === "accepted") {
                    return (
                      <button
                        onClick={() => {
                          setAutoModalCtrl(row.autoCtrl);
                          setAutoOpaPage(0);
                          setAutoModalOpen(true);
                        }}
                        className="inline-flex items-center justify-center gap-1.5 w-[110px] py-1.5 rounded-lg text-[12px] font-medium bg-indigo-600 text-white hover:opacity-90 transition-all"
                      >
                        <Check className="size-3.5" /> Approved
                      </button>
                    );
                  }
                  return (
                    <button
                      onClick={() => {
                        setAutoModalCtrl(row.autoCtrl);
                        setAutoOpaPage(0);
                        setAutoModalOpen(true);
                      }}
                      className="inline-flex items-center justify-center gap-1.5 w-[110px] py-1.5 rounded-lg text-[12px] font-medium bg-indigo-600 text-white hover:opacity-90 transition-all"
                    >
                      {hasFollowUpHistory ? (
                        <MessageSquare className="size-3.5" />
                      ) : (
                        <Eye className="size-3.5" />
                      )}
                      View
                    </button>
                  );
                }
                // Execution-view drives the trigger: state === FOLLOWUP (whether
                // declarative or D+E with needs_evidence) means assessor wants a
                // response. FOLLOWUP_RESOLVED means the system owner has already
                // responded, so fall through to the View branch.
                // `review_status === "follow_up_resolved"` overrides a lagged
                // `state === "FOLLOWUP"` — backend can update review_status first.
                const stmtStateUpper = (row.stmt.state ?? "").toUpperCase();
                const stmtResolved =
                  stmtStateUpper === "FOLLOWUP_RESOLVED" ||
                  (row.stmt.reviewStatus ?? "").toLowerCase() ===
                    "follow_up_resolved";
                if (
                  !stmtResolved &&
                  (stmtStateUpper === "FOLLOWUP" || status === "follow_up")
                ) {
                  return (
                    <button
                      onClick={() => {
                        // Open the View modal — it already embeds the
                        // FollowUpPanel (assessor question + prior response +
                        // evidence) and exposes a "Respond to Follow-Up"
                        // action that launches the focused response modal.
                        setViewModalCtrl(row.ctrl);
                        setViewModalStmt(row.stmt);
                      }}
                      className="inline-flex items-center justify-center w-[110px] py-1.5 rounded-lg text-[12px] font-bold bg-amber-500 dark:bg-amber-600 text-white hover:opacity-90 transition-all"
                    >
                      Respond
                    </button>
                  );
                }
                if (row.stmt.state && row.stmt.state !== "NEW") {
                  return (
                    <button
                      onClick={() => {
                        setViewModalCtrl(row.ctrl);
                        setViewModalStmt(row.stmt);
                      }}
                      className="inline-flex items-center justify-center gap-1.5 w-[110px] py-1.5 rounded-lg text-[12px] font-medium bg-indigo-600 text-white hover:opacity-90 transition-all"
                    >
                      <Eye className="size-3.5" /> View
                    </button>
                  );
                }
                if (status === "rejected")
                  return (
                    <button
                      onClick={() => {
                        setModalCtrlId(row.ctrl.id);
                        setModalStmtId(row.stmt.id);
                        const hasEvidence =
                          evidenceLinks.some(
                            (l) => l.statementId === row.stmt.label,
                          ) || (declEvAttached[row.stmt.id] ?? []).length > 0;
                        setModalTab(
                          hasEvidence ? "declarative_evidence" : "declarative",
                        );
                        setAssessModalOpen(true);
                      }}
                      className="inline-flex items-center justify-center gap-1.5 w-[110px] py-1.5 rounded-lg text-[12px] font-medium bg-indigo-600 text-white hover:opacity-90 transition-all"
                    >
                      <XCircle className="size-3.5" /> Rejected
                    </button>
                  );
                if (status === "accepted")
                  return (
                    <button
                      onClick={() => {
                        setModalCtrlId(row.ctrl.id);
                        setModalStmtId(row.stmt.id);
                        const hasEvidence =
                          evidenceLinks.some(
                            (l) => l.statementId === row.stmt.label,
                          ) || (declEvAttached[row.stmt.id] ?? []).length > 0;
                        setModalTab(
                          hasEvidence ? "declarative_evidence" : "declarative",
                        );
                        setAssessModalOpen(true);
                      }}
                      className="inline-flex items-center justify-center gap-1.5 w-[110px] py-1.5 rounded-lg text-[12px] font-medium bg-indigo-600 text-white hover:opacity-90 transition-all"
                    >
                      <Check className="size-3.5" /> Approved
                    </button>
                  );
                if (status === "completed")
                  return (
                    <button
                      onClick={() => {
                        setModalCtrlId(row.ctrl.id);
                        setModalStmtId(row.stmt.id);
                        const hasEvidence =
                          evidenceLinks.some(
                            (l) => l.statementId === row.stmt.label,
                          ) || (declEvAttached[row.stmt.id] ?? []).length > 0;
                        setModalTab(
                          hasEvidence ? "declarative_evidence" : "declarative",
                        );
                        setAssessModalOpen(true);
                      }}
                      className="inline-flex items-center justify-center gap-1.5 w-[110px] py-1.5 rounded-lg text-[12px] font-medium bg-indigo-600 text-white hover:opacity-90 transition-all"
                    >
                      {hasFollowUpHistory ? (
                        <MessageSquare className="size-3.5" />
                      ) : (
                        <Eye className="size-3.5" />
                      )}
                      View
                    </button>
                  );
                return (
                  <button
                    onClick={() => {
                      setModalCtrlId(row.ctrl.id);
                      setModalStmtId(row.stmt.id);
                      const method =
                        row.stmt.assessmentMethods?.[0] ?? "declarative";
                      setModalTab(
                        method === "declarative_evidence"
                          ? "declarative_evidence"
                          : "declarative",
                      );
                      setDeEvStep(1);
                      setDeEvSelectedFiles([]);
                      setDeEvSelectedExisting(new Set());
                      setDeEvExistingSearch("");
                      setAttachSelectedIds(new Set());
                      setEvidenceSearch("");
                      // Reset D+E form for a fresh entry
                      setEvidenceTitle("");
                      setEvidenceTags("");
                      updateDeclEvForm(row.stmt.id, {
                        determination: null,
                        evidenceIds: [],
                        justification: "",
                      });
                      setAssessModalOpen(true);
                    }}
                    className="inline-flex items-center justify-center w-[110px] py-1.5 rounded-lg text-[12px] font-medium bg-indigo-600 text-white hover:opacity-90 transition-all"
                  >
                    Assess
                  </button>
                );
                })();
                const canValidate = resolveRowEvidence(row) !== null;
                const rowKey = rowValidationKey(row);
                const isValidatingRow =
                  validationLoading && validationRowKey === rowKey;
                return (
                  <div className="flex flex-col items-center justify-center gap-1.5">
                    {primaryAction}
                    {canValidate && (
                      <button
                        onClick={() => handleValidateEvidence(row)}
                        disabled={isValidatingRow}
                        title="Run AI evidence validation"
                        className="inline-flex items-center justify-center gap-1.5 w-[110px] py-1.5 rounded-lg text-[12px] font-medium bg-white dark:bg-white/[0.04] text-indigo-700 dark:text-violet-300 border border-indigo-200 dark:border-violet-500/30 hover:bg-indigo-50 dark:hover:bg-violet-500/10 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        {isValidatingRow ? (
                          <Loader2 className="size-3.5 animate-spin" />
                        ) : (
                          <Sparkles className="size-3.5" />
                        )}
                        Validate
                      </button>
                    )}
                  </div>
                );
              },
            },
            {
              key: "status",
              header: (
                <button
                  onClick={() => handleTableSort("status")}
                  className="flex w-full items-center justify-center hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
                >
                  Status
                  <SortIcon col="status" />
                </button>
              ),
              align: "center",
              render: (row) => {
                const stateBadge = (
                  state: string | undefined,
                  verdict?: string,
                ) => {
                  const cfg: { label: string; color: string } = (() => {
                    switch ((state ?? "").toUpperCase()) {
                      case "NEW":
                        return {
                          label: "Not Assessed",
                          color:
                            "bg-slate-100 dark:bg-slate-500/10 text-slate-500 dark:text-slate-400 border border-slate-200 dark:border-slate-500/20",
                        };
                      case "AWAITING_REVIEW":
                        return {
                          label: "Awaiting Review",
                          color:
                            "bg-blue-50 dark:bg-blue-500/10 text-blue-700 dark:text-blue-400 border border-blue-200 dark:border-blue-500/20",
                        };
                      case "FOLLOWUP":
                        return {
                          label: "Follow-Up Required",
                          color:
                            "bg-orange-50 dark:bg-orange-500/10 text-orange-700 dark:text-orange-400 border border-orange-200 dark:border-orange-500/20",
                        };
                      case "FOLLOWUP_RESOLVED":
                        return {
                          label: "Response Submitted",
                          color:
                            "bg-sky-50 dark:bg-sky-500/10 text-sky-700 dark:text-sky-400 border border-sky-200 dark:border-sky-500/20",
                        };
                      case "REVIEWED": {
                        const v = (verdict ?? "").toLowerCase();
                        if (
                          v === "pass" ||
                          v === "passed" ||
                          v === "satisfied"
                        ) {
                          return {
                            label: "Satisfied",
                            color:
                              "bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-500/20",
                          };
                        }
                        if (
                          v === "fail" ||
                          v === "failed" ||
                          v === "rejected"
                        ) {
                          return {
                            label: "Not Satisfied",
                            color:
                              "bg-red-50 dark:bg-red-500/10 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-500/20",
                          };
                        }
                        return {
                          label: "Reviewed",
                          color:
                            "bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-500/20",
                        };
                      }
                      default:
                        return {
                          label: "Not Assessed",
                          color:
                            "bg-slate-100 dark:bg-slate-500/10 text-slate-500 dark:text-slate-400 border border-slate-200 dark:border-slate-500/20",
                        };
                    }
                  })();
                  return (
                    <span
                      className={`inline-flex items-center justify-center px-2 py-1 rounded text-[12px] font-bold whitespace-nowrap ${cfg.color}`}
                    >
                      {cfg.label}
                    </span>
                  );
                };

                if (row.kind === "declarative") {
                  // Execution-view's `review_status` is the resolution truth;
                  // `state` can lag at FOLLOWUP after the user has responded.
                  if (
                    (row.stmt.reviewStatus ?? "").toLowerCase() ===
                    "follow_up_resolved"
                  ) {
                    return stateBadge("FOLLOWUP_RESOLVED", row.stmt.verdict);
                  }
                  return stateBadge(row.stmt.state, row.stmt.verdict);
                }

                const stmts = row.autoCtrl.executionViewStatements ?? [];
                // Promote per-statement to FOLLOWUP_RESOLVED when review_status says so.
                const effectiveStates = stmts.map((s) =>
                  (s.review_status ?? "").toLowerCase() === "follow_up_resolved"
                    ? "FOLLOWUP_RESOLVED"
                    : (s.state ?? "").toUpperCase(),
                );
                if (
                  effectiveStates.length > 0 &&
                  effectiveStates.every((st) => st && st !== "NEW")
                ) {
                  const priorityOrder = [
                    "FOLLOWUP",
                    "AWAITING_REVIEW",
                    "FOLLOWUP_RESOLVED",
                    "REVIEWED",
                  ];
                  const primary =
                    priorityOrder.find((p) => effectiveStates.includes(p)) ??
                    effectiveStates[0];
                  const primaryIdx = effectiveStates.findIndex(
                    (st) => st === primary,
                  );
                  const verdict =
                    primaryIdx >= 0 ? stmts[primaryIdx]?.verdict : undefined;
                  return stateBadge(primary, verdict);
                }

                const status = getRowStatus(row);
                if (status === "auto_passed")
                  return (
                    <span className="inline-flex items-center justify-center px-2 py-1 rounded text-[12px] font-bold whitespace-nowrap bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-500/20">
                      Satisfied
                    </span>
                  );
                if (status === "auto_failed")
                  return (
                    <span className="inline-flex items-center justify-center px-2 py-1 rounded text-[12px] font-bold whitespace-nowrap bg-red-50 dark:bg-red-500/10 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-500/20">
                      Not-Satisfied
                    </span>
                  );
                return stateBadge("NEW");
              },
            },
          ];

          if (loading || !reviewStatusLoaded) {
            return (
              <div className="flex flex-col items-center justify-center gap-3 py-24">
                <Loader2 className="size-8 animate-spin text-indigo-500 dark:text-indigo-400" />
                <p className="text-sm text-muted-foreground">
                  Loading assessment data
                </p>
              </div>
            );
          }

          return (
            <div
              className={`flex flex-col flex-1 min-h-0 gap-4 ${allAccepted ? "pointer-events-none select-none" : ""}`}
            >
              {/* Search + Kind filter toolbar */}
              <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                <div className="relative flex-1 min-w-[200px] max-w-sm">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground/50 pointer-events-none" />
                  <input
                    type="text"
                    placeholder="Search by control ID, title, or statement"
                    value={tableSearch}
                    onChange={(e) => {
                      setTableSearch(e.target.value);
                      setTablePage(0);
                    }}
                    className="w-full h-[34px] pl-8 pr-3 text-[13px] bg-[#f8fafc] dark:bg-white/[0.04] border border-[#e2e8f0] dark:border-white/[0.06] rounded-lg outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 dark:focus:border-indigo-500 transition-all placeholder:text-muted-foreground/40"
                  />
                </div>
                <div className="flex items-center gap-2 ml-auto flex-shrink-0">
                  <label className="text-sm text-muted-foreground whitespace-nowrap">
                    Rows per page
                  </label>
                  <Dropdown
                    value={String(tablePageSize)}
                    onChange={(v) => {
                      setTablePageSize(Number(v));
                      setTablePage(0);
                    }}
                    options={[10, 25, 50, 100].map((n) => ({ value: String(n), label: String(n) }))}
                    size="sm"
                    align="end"
                    className="!min-w-[80px]"
                  />
                </div>
              </div>
              <div className="flex-1 min-h-0 flex flex-col">
                <div className="flex-1 min-h-0">
                  <DataTable
                    data={sortedRows.slice(
                      tablePage * tablePageSize,
                      (tablePage + 1) * tablePageSize,
                    )}
                    columns={columns}
                    page={tablePage}
                    onPageChange={setTablePage}
                    pageSize={tablePageSize}
                    totalItems={sortedRows.length}
                    emptyText={
                      rows.length === 0
                        ? "No controls in this plan"
                        : "No controls match your search"
                    }
                  />
                </div>
              </div>
            </div>
          );
        })()}
      </div>

      {/* ══════ ASSESSMENT MODAL ══════ */}
      {assessModalOpen &&
        modalCtrlId &&
        modalStmtId &&
        (() => {
          const mCtrl = getModalControl();
          const mStmt = getModalStatement();
          if (!mCtrl || !mStmt) return null;

          const declMethod =
            mStmt.methods.find((m) => m.type === "declarative") ?? null;
          const deEvForm = getDeclEvForm(modalStmtId);
          const deEvAttachedList = declEvAttached[modalStmtId] ?? [];
          const declResult = declResults.find(
            (r) => r.execution?.statementId === mStmt.label,
          );
          const apiResult = declResult;
          const isSaved =
            !deEvSaveLoading &&
            (savedStatements.has(`${modalCtrlId}:${modalStmtId}`) ||
              !!declResult);
          const isDeEv = modalTab === "declarative_evidence";
          const canSaveDeclTab = !!declMethod?.reason?.trim();
          const wordCount = countWords(
            modalTab === "declarative"
              ? (declMethod?.reason ?? "")
              : deEvForm.justification,
          );
          const selectedExistingEvidenceTitle = [...deEvSelectedExisting]
            .map(
              (evidenceId) =>
                evidenceSearchResults.find((e) => e.id === evidenceId) ||
                evidenceCache[evidenceId],
            )
            .find((evidence) => evidence?.title)?.title;
          const reviewEvidenceTitle =
            evidenceTitle.trim() ||
            selectedExistingEvidenceTitle ||
            deEvSelectedFiles[0]?.name ||
            "-";

          return (
            <ModalShell open onClose={() => setAssessModalOpen(false)}>
              {/* Modal Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-[#e2e8f0] dark:border-white/[0.06] flex-shrink-0">
                <div className="flex items-center gap-3">
                  <span className="px-2 py-0.5 rounded text-[12px] font-bold bg-indigo-50 dark:bg-indigo-500/10 text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-500/20">
                    {mCtrl.controlId?.toUpperCase()}
                  </span>
                  <span className="text-[15px] font-medium text-[#0f172a] dark:text-white">
                    {mStmt.label?.toUpperCase()}
                  </span>
                </div>
                <button
                  onClick={() => setAssessModalOpen(false)}
                  className="p-1.5 rounded-lg hover:bg-[#f1f5f9] dark:hover:bg-white/[0.06] transition-colors"
                >
                  <X className="size-5 text-[#64748b] dark:text-white/40" />
                </button>
              </div>

              {/* Modal Body */}
              <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5 space-y-5">
                {/* Statement text */}
                <div className="rounded-lg bg-[#f8fafc] dark:bg-white/[0.03] border border-[#e2e8f0] dark:border-white/[0.06] px-4 py-3">
                  <p className="text-[13px] text-[#334155] dark:text-white/70 leading-relaxed">
                    {mStmt.text}
                  </p>
                </div>

                {/* Method Toggle */}
                {!isSaved && (
                  <div>
                    <p className="text-[13px] font-semibold text-[#cbd5e1] dark:text-white/80 mb-2">
                      Assessment Method
                    </p>
                    <div className="flex gap-2 flex-wrap">
                      {(["declarative", "declarative_evidence"] as const).map(
                        (method) => {
                          const cfg = METHOD_CONFIG[method];
                          const isSelected = modalTab === method;
                          return (
                            <button
                              key={method}
                              onClick={() => {
                                setModalTab(method);
                                if (method === "declarative_evidence")
                                  setDeEvStep(1);
                              }}
                              className={`flex-1 min-w-[120px] px-4 py-2.5 rounded-lg text-[13px] font-bold transition-all border flex items-center gap-2 justify-center ${
                                isSelected
                                  ? "bg-indigo-600 dark:bg-indigo-600 text-white border-indigo-600 dark:border-indigo-600"
                                  : "bg-white dark:bg-[#131320] text-[#475569] dark:text-white/50 border-[#e2e8f0] dark:border-white/[0.08] hover:border-indigo-300 dark:hover:border-indigo-500/30"
                              }`}
                            >
                              <cfg.icon className="size-4" />
                              {cfg.label}
                            </button>
                          );
                        },
                      )}
                    </div>
                  </div>
                )}

                {/* Saved method label */}
                {isSaved && (
                  <div className="flex items-center gap-2 px-4 py-3 rounded-lg bg-[#f8fafc] dark:bg-white/[0.03] border border-[#e2e8f0] dark:border-white/[0.06]">
                    <FileText className="size-4 text-indigo-500 dark:text-indigo-400" />
                    <span className="text-[13px] font-semibold text-indigo-600 dark:text-indigo-400">
                      {deEvAttachedList.length > 0
                        ? "Declarative + Evidence"
                        : "Declarative"}
                    </span>
                  </div>
                )}

                {/* D+E Step indicator */}
                {!isSaved && isDeEv && (
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2">
                      <span
                        className={`size-7 rounded-full flex items-center justify-center text-[12px] font-bold ${deEvStep === 1 ? "bg-indigo-600 dark:bg-indigo-600 text-white ring-4 ring-indigo-100 dark:ring-indigo-500/20" : "bg-emerald-500 text-white"}`}
                      >
                        {deEvStep > 1 ? <Check className="size-4" /> : "1"}
                      </span>
                      <span
                        className={`text-[13px] font-semibold ${deEvStep === 1 ? "text-[#0f172a] dark:text-white" : "text-emerald-600 dark:text-emerald-400"}`}
                      >
                        Details
                      </span>
                    </div>
                    <div
                      className={`flex-1 h-px ${deEvStep > 1 ? "bg-emerald-400 dark:bg-emerald-500/40" : "bg-[#e2e8f0] dark:bg-white/[0.08]"}`}
                    />
                    <div className="flex items-center gap-2">
                      <span
                        className={`size-7 rounded-full flex items-center justify-center text-[12px] font-bold ${deEvStep === 2 ? "bg-indigo-600 dark:bg-indigo-600 text-white ring-4 ring-indigo-100 dark:ring-indigo-500/20" : "bg-[#e2e8f0] dark:bg-white/[0.1] text-[#64748b] dark:text-white/40"}`}
                      >
                        2
                      </span>
                      <span
                        className={`text-[13px] font-semibold ${deEvStep === 2 ? "text-[#0f172a] dark:text-white" : "text-[#64748b] dark:text-white/40"}`}
                      >
                        Review
                      </span>
                    </div>
                  </div>
                )}

                {/* ═══ DECLARATIVE MODE ═══ */}
                {!isSaved && !isDeEv && (
                  <>
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-[13px] font-semibold text-[#cbd5e1] dark:text-white/80">
                          Justification For Choosing Declarative
                        </p>
                        <span className="text-[12px] font-medium text-[#94a3b8] dark:text-white/60">
                          {wordCount} Words
                        </span>
                      </div>
                      <textarea
                        placeholder="Enter formal justification for the determination here"
                        value={declMethod?.reason ?? ""}
                        onChange={(e) =>
                          updateMethod(
                            modalCtrlId!,
                            modalStmtId!,
                            "declarative",
                            { reason: e.target.value },
                          )
                        }
                        rows={4}
                        className="w-full text-[13px] bg-white dark:bg-[#131320] rounded-lg px-4 py-2.5 outline-none border border-[#e2e8f0] dark:border-white/[0.07] focus:ring-2 focus:ring-indigo-500/20 dark:focus:ring-indigo-500/20 focus:border-indigo-500 dark:focus:border-indigo-500 transition-all placeholder:text-[#94a3b8] dark:placeholder:text-white/60 resize-y leading-relaxed text-[#0f172a] dark:text-white"
                      />
                    </div>
                    <div className="flex justify-end pt-2">
                      <button
                        onClick={async () => {
                          await handleSaveModal();
                          setAssessModalOpen(false);
                        }}
                        disabled={savingModal || !canSaveDeclTab}
                        className={`px-6 py-2.5 rounded-lg text-[13px] font-bold transition-all flex items-center gap-2 ${savingModal || !canSaveDeclTab ? "bg-[#e2e8f0] dark:bg-white/[0.06] text-[#94a3b8] dark:text-white/30 cursor-not-allowed" : "bg-indigo-600 dark:bg-indigo-600 text-white hover:bg-indigo-700 dark:hover:bg-indigo-700 shadow-lg shadow-indigo-500/20 dark:shadow-indigo-500/20"}`}
                      >
                        {savingModal && (
                          <Loader2 className="size-4 animate-spin" />
                        )}
                        Submit
                      </button>
                    </div>
                  </>
                )}

                {/* ═══ D+E STEP 1 ═══ */}
                {!isSaved && isDeEv && deEvStep === 1 && (
                  <>
                    <div className="rounded-xl border border-indigo-200 dark:border-indigo-500/20 bg-indigo-50/30 dark:bg-indigo-500/[0.03] p-4 space-y-3">
                      <div>
                        <p className="text-[13px] font-semibold text-indigo-700 dark:text-indigo-300">
                          Add New Evidence
                        </p>
                        <p className="mt-0.5 text-[11px] text-[#64748b] dark:text-white/50">
                          Give it a title and tags, then upload the file.
                        </p>
                      </div>

                      <div className="flex flex-col gap-3 sm:flex-row">
                          <div className="flex-1 space-y-1.5">
                            <label className="text-[13px] font-semibold text-[#cbd5e1] dark:text-white/80">
                              Title <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              maxLength={200}
                              placeholder="Evidence title"
                              value={evidenceTitle}
                              onChange={(e) => setEvidenceTitle(e.target.value)}
                              className="w-full text-[13px] bg-white dark:bg-[#131320] rounded-lg px-3 py-2.5 outline-none border border-[#e2e8f0] dark:border-white/[0.07] focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all placeholder:text-[#94a3b8] dark:placeholder:text-white/60 text-[#0f172a] dark:text-white"
                            />
                          </div>
                          <div className="flex-1 space-y-1.5">
                            <label className="text-[13px] font-semibold text-[#cbd5e1] dark:text-white/80">
                              Tags <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              maxLength={200}
                              placeholder="Comma-separated tags"
                              value={evidenceTags}
                              onChange={(e) => setEvidenceTags(e.target.value)}
                              className="w-full text-[13px] bg-white dark:bg-[#131320] rounded-lg px-3 py-2.5 outline-none border border-[#e2e8f0] dark:border-white/[0.07] focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all placeholder:text-[#94a3b8] dark:placeholder:text-white/60 text-[#0f172a] dark:text-white"
                            />
                          </div>
                        </div>

                      <div
                        onClick={() => {
                          if (deEvSelectedFiles.length > 0) return;
                          const input = document.createElement("input");
                          input.type = "file";
                          input.accept =
                            ".pdf,.json,.csv,.xlsx,.xls,.doc,.docx,.txt,.png,.jpg,.jpeg,.xml,.yaml,.yml";
                          input.onchange = () => {
                            const files = input.files;
                            if (files && files.length > 0)
                              setDeEvSelectedFiles([files[0]]);
                          };
                          input.click();
                        }}
                        className={`flex flex-col items-center justify-center gap-2 px-4 ${deEvSelectedFiles.length > 0 ? "py-3" : "py-6"} rounded-xl border-2 border-dashed transition-all ${deEvSelectedFiles.length > 0 ? "border-emerald-400 dark:border-emerald-500/30 bg-emerald-50/50 dark:bg-emerald-500/[0.04]" : "border-indigo-300 dark:border-indigo-500/30 hover:border-indigo-400 cursor-pointer"}`}
                      >
                        {deEvSelectedFiles.length > 0 && (
                          <div className="w-full" onClick={(e) => e.stopPropagation()}>
                            <div className="flex items-center justify-between px-3 py-2 rounded-lg bg-emerald-100/50 dark:bg-emerald-500/10 border border-emerald-300 dark:border-emerald-500/20 text-[12px]">
                              <div className="flex items-center gap-2 min-w-0">
                                <FileText className="size-3.5 text-emerald-600 flex-shrink-0" />
                                <span className="text-emerald-800 dark:text-emerald-300 font-medium truncate">{deEvSelectedFiles[0].name}</span>
                                <span className="text-[12px] text-emerald-600/60 flex-shrink-0">{(deEvSelectedFiles[0].size / 1024).toFixed(1)} KB</span>
                              </div>
                              <button onClick={() => setDeEvSelectedFiles([])} className="p-1 rounded text-emerald-400 hover:text-red-500 transition-colors flex-shrink-0">
                                <X className="size-3.5" />
                              </button>
                            </div>
                          </div>
                        )}
                        {deEvSelectedFiles.length === 0 && (
                          <>
                            <Upload className="size-5 text-indigo-500 dark:text-indigo-400" />
                            <p className="text-[12px] font-medium text-indigo-600 dark:text-indigo-400">Click to upload an evidence file</p>
                            <p className="text-[10px] text-[#475569] dark:text-white/60">Supported: PDF, JSON, CSV, XLSX, TXT, PNG, JPG, XML, YAML</p>
                          </>
                        )}
                      </div>
                    </div>

                    <div className="rounded-xl border border-[#e2e8f0] dark:border-white/[0.08] bg-white/50 dark:bg-white/[0.02] p-4">
                      <div className="flex items-start justify-between gap-3 mb-2">
                        <div className="min-w-0">
                          <p className="text-[13px] font-semibold text-[#cbd5e1] dark:text-white/80">
                            Select Existing Evidence
                            {deEvSelectedExisting.size > 0 && (
                              <span className="ml-2 px-1.5 py-0.5 rounded text-[11px] font-bold bg-indigo-100 dark:bg-indigo-500/15 text-indigo-600 dark:text-indigo-400">
                                {deEvSelectedExisting.size} selected
                              </span>
                            )}
                          </p>
                          <p className="mt-0.5 text-[11px] text-[#64748b] dark:text-white/50">
                            No title or tags are required here existing evidence uses its saved metadata
                          </p>
                        </div>
                        {deEvSelectedExisting.size > 0 && (
                          <button
                            onClick={() => setDeEvSelectedExisting(new Set())}
                            className="flex-shrink-0 text-[11px] text-[#94a3b8] dark:text-white/40 hover:text-red-500 dark:hover:text-red-400 transition-colors"
                          >
                            Clear all
                          </button>
                        )}
                      </div>
                      <div className="relative mb-2">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-3.5 text-[#94a3b8]" />
                        <input
                          type="text"
                          placeholder="Search evidence by title or tag..."
                          value={deEvExistingSearch}
                          onChange={(e) => {
                            setDeEvExistingSearch(e.target.value);
                            searchEvidence(e.target.value);
                          }}
                          className="w-full pl-8 pr-3 py-2 text-[12px] bg-white dark:bg-[#131320] rounded-lg border border-[#e2e8f0] dark:border-white/[0.07] outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all placeholder:text-[#94a3b8] dark:placeholder:text-white/40 text-[#0f172a] dark:text-white"
                        />
                      </div>
                      <div className="max-h-[204px] overflow-y-auto space-y-1.5 pr-1.5">
                        {evidenceSearchLoading && (
                          <div className="flex items-center justify-center py-6">
                            <Loader2 className="size-4 animate-spin text-indigo-400" />
                          </div>
                        )}
                        {!evidenceSearchLoading && evidenceSearchResults.filter((ev) => ev.hasFile).length === 0 && (
                          <p className="text-[12px] text-center text-[#94a3b8] dark:text-white/40 py-5">No evidence found</p>
                        )}
                        {!evidenceSearchLoading && evidenceSearchResults.filter((ev) => ev.hasFile).map((ev) => {
                          const sel = deEvSelectedExisting.has(ev.id);
                          return (
                            <div
                              key={ev.id}
                              onClick={() => setDeEvSelectedExisting((prev) => {
                                const next = new Set(prev);
                                if (next.has(ev.id)) next.delete(ev.id); else next.add(ev.id);
                                return next;
                              })}
                              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border cursor-pointer transition-all ${sel ? "border-indigo-400 dark:border-indigo-500/50 bg-indigo-50/60 dark:bg-indigo-500/10" : "border-[#e2e8f0] dark:border-white/[0.06] hover:border-indigo-300 dark:hover:border-indigo-500/30 bg-white dark:bg-white/[0.02] hover:bg-[#f8fafc] dark:hover:bg-white/[0.04]"}`}
                            >
                              <div className={`flex-shrink-0 w-4 h-4 rounded border-2 flex items-center justify-center transition-all ${sel ? "bg-indigo-500 border-indigo-500" : "border-[#cbd5e1] dark:border-white/20"}`}>
                                {sel && <Check className="size-2.5 text-white" />}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-[12px] font-medium text-[#0f172a] dark:text-white truncate">{ev.title}</p>
                                {ev.tags && ev.tags.length > 0 && (
                                  <div className="flex items-center gap-1 mt-0.5 flex-wrap">
                                    {ev.tags.map((tag) => (
                                      <span key={tag} className="px-1.5 rounded text-[10px] bg-slate-100 dark:bg-white/[0.05] text-[#64748b] dark:text-white/50">{tag}</span>
                                    ))}
                                  </div>
                                )}
                              </div>
                              {ev.hasFile && (
                                <div className="flex-shrink-0 flex items-center gap-1 text-[10px] text-emerald-600 dark:text-emerald-400">
                                  <FileText className="size-3" />
                                  <span className="truncate max-w-[90px] hidden sm:inline">{ev.fileName}</span>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-[13px] font-semibold text-[#cbd5e1] dark:text-white/80">
                          Justification For Choosing Declarative + Evidence{" "}
                          <span className="text-red-500">*</span>
                        </p>
                        <span className="text-[12px] font-medium text-[#94a3b8] dark:text-white/60">
                          {wordCount} Words
                        </span>
                      </div>
                      <textarea
                        placeholder="Provide justification for your determination"
                        value={deEvForm.justification}
                        onChange={(e) =>
                          updateDeclEvForm(modalStmtId!, {
                            justification: e.target.value,
                          })
                        }
                        rows={4}
                        className="w-full text-[13px] bg-white dark:bg-[#131320] rounded-lg px-4 py-2.5 outline-none border border-[#e2e8f0] dark:border-white/[0.07] focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all placeholder:text-[#94a3b8] dark:placeholder:text-white/60 resize-y leading-relaxed text-[#0f172a] dark:text-white"
                      />
                    </div>
                    <div className="flex justify-end pt-2">
                      {(() => {
                        const hasNewFile = deEvSelectedFiles.length > 0;
                        const hasEvidence = hasNewFile || deEvSelectedExisting.size > 0;
                        const metadataMissing =
                          hasNewFile && (!evidenceTitle.trim() || !evidenceTags.trim());
                        const blocked =
                          !hasEvidence ||
                          metadataMissing ||
                          !deEvForm.justification?.trim();
                        return (
                          <button
                            onClick={() => setDeEvStep(2)}
                            disabled={blocked}
                            className={`px-6 py-2.5 rounded-lg text-[13px] font-bold transition-all flex items-center gap-2 ${blocked ? "bg-[#e2e8f0] dark:bg-white/[0.06] text-[#94a3b8] dark:text-white/30 cursor-not-allowed" : "bg-indigo-600 dark:bg-indigo-600 text-white hover:opacity-90"}`}
                          >
                            Next <ChevronRight className="size-3.5" />
                          </button>
                        );
                      })()}
                    </div>
                  </>
                )}

                {/* ═══ D+E STEP 2: Review ═══ */}
                {!isSaved && isDeEv && deEvStep === 2 && (
                  <div className="space-y-5">
                    <div className="rounded-xl bg-[#f8fafc] dark:bg-white/[0.03] border border-[#e2e8f0] dark:border-white/[0.06] p-5 space-y-4">
                      <div className="flex gap-4">
                        <div className="flex-1">
                          <p className="text-[13px] font-semibold text-[#cbd5e1] dark:text-white/80 mb-1">
                            Title
                          </p>
                          <p className="text-[13px] text-[#0f172a] dark:text-white font-medium">
                            {reviewEvidenceTitle}
                          </p>
                        </div>
                        {evidenceTags.trim() && (
                          <div className="flex-1">
                            <p className="text-[13px] font-semibold text-[#cbd5e1] dark:text-white/80 mb-1">
                              Tags
                            </p>
                            <div className="flex flex-wrap gap-1.5">
                              {evidenceTags
                                .split(",")
                                .map((t) => t.trim())
                                .filter(Boolean)
                                .map((tag) => (
                                  <span
                                    key={tag}
                                    className="px-2 py-0.5 rounded text-[12px] font-medium bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 border border-indigo-200 dark:border-indigo-500/20"
                                  >
                                    {tag}
                                  </span>
                                ))}
                            </div>
                          </div>
                        )}
                      </div>
                      {deEvForm.justification && (
                        <div>
                          <p className="text-[13px] font-semibold text-[#cbd5e1] dark:text-white/80 mb-1">
                            Justification
                          </p>
                          <p className="text-[13px] text-[#334155] dark:text-white/70 leading-relaxed whitespace-pre-wrap">
                            {deEvForm.justification}
                          </p>
                        </div>
                      )}
                      {deEvSelectedFiles.length > 0 && (
                        <div>
                          <p className="text-[13px] font-semibold text-[#cbd5e1] dark:text-white/80 mb-2">
                            Uploaded File
                          </p>
                          <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-50/50 dark:bg-emerald-500/[0.04] border border-emerald-200 dark:border-emerald-500/20">
                            <FileText className="size-3.5 text-emerald-600 flex-shrink-0" />
                            <span className="text-[12px] font-medium text-emerald-800 dark:text-emerald-300">
                              {deEvSelectedFiles[0].name}
                            </span>
                            <span className="text-[12px] text-emerald-600/60 ml-auto mr-2">
                              {(deEvSelectedFiles[0].size / 1024).toFixed(1)} KB
                            </span>
                            <button
                              onClick={() => {
                                const file = deEvSelectedFiles[0];
                                const url = URL.createObjectURL(file);
                                setPreview((prev) => {
                                  revokeLocalPreview(prev);
                                  return {
                                    url,
                                    title: file.name,
                                    fileName: file.name,
                                    downloadUrl: url,
                                  };
                                });
                              }}
                              className="flex items-center gap-1 px-2.5 py-1 rounded-md text-[12px] font-medium text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-500/10 hover:bg-indigo-100 dark:hover:bg-indigo-500/15 transition-colors"
                            >
                              <Eye className="size-3" /> Preview
                            </button>
                          </div>
                        </div>
                      )}
                      {deEvSelectedExisting.size > 0 && (
                        <div>
                          <p className="text-[13px] font-semibold text-[#cbd5e1] dark:text-white/80 mb-2">
                            Selected Evidence
                          </p>
                          <div className="space-y-2">
                            {[...deEvSelectedExisting].map((eid) => {
                              const ev =
                                evidenceSearchResults.find((e) => e.id === eid) ||
                                evidenceCache[eid];
                              const title = ev?.title || eid;
                              const fileName = ev?.fileName;
                              return (
                                <div
                                  key={eid}
                                  className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white dark:bg-white/[0.03] border border-[#e2e8f0] dark:border-white/[0.08]"
                                >
                                  <FileText className="size-3.5 text-[#64748b] dark:text-white/50 flex-shrink-0" />
                                  <div className="min-w-0">
                                    <p className="text-[12px] font-medium text-[#0f172a] dark:text-white truncate">
                                      {title}
                                    </p>
                                    {fileName && (
                                      <p className="text-[11px] text-[#64748b] dark:text-white/50 truncate">
                                        {fileName}
                                      </p>
                                    )}
                                  </div>
                                  <button
                                    onClick={() => handlePreview(eid, title)}
                                    className="flex items-center gap-1 px-2.5 py-1 rounded-md text-[12px] font-medium text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-500/10 hover:bg-indigo-100 dark:hover:bg-indigo-500/15 transition-colors ml-auto flex-shrink-0"
                                  >
                                    <Eye className="size-3" /> Preview
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="flex justify-between items-center">
                      <button
                        onClick={() => setDeEvStep(1)}
                        className="flex items-center gap-1 px-4 py-2.5 rounded-lg text-[13px] font-medium text-[#475569] dark:text-white/80 bg-[#f1f5f9] dark:bg-white/[0.06] border border-[#e2e8f0] dark:border-white/[0.10] hover:bg-[#e2e8f0] dark:hover:bg-white/[0.10] transition-colors"
                      >
                        <ChevronLeft className="size-3.5" /> Back
                      </button>
                      <button
                        onClick={async () => {
                          if (!id || !modalCtrlId || !modalStmtId) return;
                          setDeEvSaveLoading(true);
                          try {
                            const form = getDeclEvForm(modalStmtId);
                            const stmtLabel = mStmt.label;
                            const evidenceIds = new Set<string>(
                              deEvSelectedExisting,
                            );

                            for (const file of deEvSelectedFiles) {
                              const tags = evidenceTags.trim()
                                ? evidenceTags
                                    .split(",")
                                    .map((t) => t.trim())
                                    .filter(Boolean)
                                : [];
                              const createRes =
                                await createSupportingEvidenceWithFile({
                                  title: evidenceTitle.trim() || file.name,
                                  file,
                                  tags,
                                  owningOrgUnitId:
                                    owningOrgUnitId || undefined,
                                });
                              const created = createRes.data;
                              setEvidenceCache((prev) => ({
                                ...prev,
                                [created.id]: created,
                              }));
                              setDeclEvAttached((prev) => ({
                                ...prev,
                                [modalStmtId!]: [
                                  ...(prev[modalStmtId!] ?? []),
                                  {
                                    id: created.id,
                                    title: created.title,
                                    fileName: created.fileName,
                                    isNew: true,
                                  },
                                ],
                              }));
                              evidenceIds.add(created.id);
                            }

                            if (evidenceIds.size > 0) {
                              await linkEvidenceToStatement(id, {
                                statementId: stmtLabel,
                                evidenceIds: [...evidenceIds],
                              });
                            }

                            // Call the new statement execution endpoint with declarative_evidence method
                            // Scenario C: Manual with file, first save
                            // Scenario E: Followup re-save with evidence
                            const payload: StatementExecutionPayload = {
                              execution_method: "declarative_evidence",
                              justification: form.justification,
                            };

                            await executeStatement(id, modalStmtId, payload);

                            // Refresh linked evidence and execution status.
                            // refreshExecutionView() updates controlsState so
                            // the status badge + Action column reflect the
                            // new state without a manual page refresh.
                            await fetchLinkedEvidence();
                            await Promise.all([
                              refreshExecutionView(),
                              fetchReviewStatus(),
                            ]);
                            setAssessModalOpen(false);
                            setSavedStatements((prev) =>
                              new Set(prev).add(
                                `${modalCtrlId}:${modalStmtId}`,
                              ),
                            );
                            setDeEvSelectedFiles([]);
                            setDeEvSelectedExisting(new Set());
                            setDeEvStep(1);
                            useToastStore
                              .getState()
                              .showSuccess(
                                "Statement Saved",
                                "Declarative assessment with evidence saved successfully",
                              );
                          } catch (err: any) {
                            const apiMsg =
                              err?.responseBody?.errors?.[0]?.detail ||
                              err?.responseBody?.errors?.[0]?.title ||
                              err?.message;
                            useToastStore
                              .getState()
                              .showError(
                                "Error",
                                apiMsg || "Failed to save statement",
                              );
                            console.error("Save D+E failed:", err);
                          }
                          setDeEvSaveLoading(false);
                        }}
                        disabled={deEvSaveLoading}
                        className={`px-6 py-2.5 rounded-lg text-[13px] font-bold transition-all flex items-center gap-2 ${deEvSaveLoading ? "bg-[#e2e8f0] dark:bg-white/[0.06] text-[#94a3b8] cursor-not-allowed" : "bg-indigo-600 dark:bg-indigo-600 text-white hover:bg-indigo-700 shadow-lg shadow-indigo-500/20"}`}
                      >
                        {deEvSaveLoading && (
                          <Loader2 className="size-4 animate-spin" />
                        )}
                        Save
                      </button>
                    </div>
                  </div>
                )}

                {/* ═══ SAVED: Read-only ═══ */}
                {isSaved &&
                  (() => {
                    return (
                      <>
                        {apiResult?.execution?.justification && (
                          <div>
                            <p className="text-[13px] font-semibold text-[#cbd5e1] dark:text-white/80 mb-2">
                              Justification
                            </p>
                            <div className="px-4 py-3 rounded-xl bg-[#f8fafc] dark:bg-white/[0.03] border border-[#e2e8f0] dark:border-white/[0.06]">
                              <p className="text-[13px] text-[#0f172a] dark:text-white/80 leading-relaxed whitespace-pre-wrap">
                                {apiResult.execution.justification}
                              </p>
                            </div>
                          </div>
                        )}
                        {deEvAttachedList.length > 0 && (
                          <div>
                            <p className="text-[13px] font-semibold text-[#cbd5e1] dark:text-white/80 mb-2">
                              Evidence ({deEvAttachedList.length})
                            </p>
                            <div className="space-y-1.5">
                              {deEvAttachedList.map((ev) => {
                                const evidenceLabel =
                                  ev.title || `Evidence ${ev.id.slice(0, 8)}`;

                                return (
                                  <div
                                    key={ev.id}
                                    className="flex items-center justify-between px-3 py-2 rounded-lg bg-[#f8fafc] dark:bg-white/[0.03] border border-[#e2e8f0] dark:border-white/[0.06] text-[12px]"
                                  >
                                    <div className="flex items-start gap-2 min-w-0">
                                      <FileText className="size-3.5 text-indigo-500 flex-shrink-0" />
                                      <span
                                        className="text-[#0f172a] dark:text-white/80 font-medium leading-snug break-words"
                                        title={evidenceLabel}
                                      >
                                        {evidenceLabel}
                                      </span>
                                    </div>
                                    {(evidenceCache[ev.id]?.hasFile ||
                                      ev.fileName) && (
                                      <button
                                        onClick={() =>
                                          handlePreview(ev.id, evidenceLabel)
                                        }
                                        className="flex items-center gap-1 px-2.5 py-1 rounded-md text-[12px] font-medium text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-500/10 hover:bg-indigo-100 transition-colors"
                                      >
                                        <Eye className="size-3" /> Preview
                                      </button>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                        {/* Unified follow-up panel (owner variant). Replaces
                              the legacy inline Follow-Up Raised block. */}
                        {(() => {
                          const modalExec = executions.find(
                            (e) => e.statementId === mStmt.label,
                          );
                          if (!modalExec) return null;
                          const fu = getStatementFollowUp(modalExec.id);
                          const resolved = getResolvedFollowUp(modalExec.id);
                          const isRaised =
                            modalExec.reviewStatus === "follow_up_raised";
                          if (!fu && !resolved && !isRaised) return null;
                          // Pull iterations[] for THIS statement directly from
                          // the controls map. Question = latest reviewer_note;
                          // response = latest iteration's justification. We
                          // never read from the outer statement.justification
                          // / statement.note — see iterations[] flow doc.
                          let stmtIters: import("../../api/frameworksApi").AssessmentExecutionViewIteration[] =
                            [];
                          for (const ctrl of controlsState) {
                            const st = (ctrl.statements ?? []).find(
                              (s: ExecutionStatement) =>
                                s.label === mStmt.label ||
                                s.id === modalExec.id,
                            );
                            if (st) {
                              stmtIters = st.iterations ?? [];
                              break;
                            }
                          }
                          const latestStmtIter =
                            stmtIters.length > 0
                              ? stmtIters[stmtIters.length - 1]
                              : null;
                          const latestQuestionIter = [...stmtIters]
                            .reverse()
                            .find((it) => it.reviewer_note);
                          const question =
                            latestQuestionIter?.reviewer_note ||
                            fu?.followUpQuestion ||
                            resolved?.question ||
                            null;
                          if (!question) return null;
                          const response =
                            latestStmtIter?.justification?.trim() || null;
                          const ri = reviewItems.find(
                            (r) => r.execution?.id === modalExec.id,
                          );
                          const chatEntries = (ri?.reviewHistory ?? []).filter(
                            (h) => h.followUpQuestion || h.followUpResponse,
                          );
                          const fuStatus =
                            isRaised && !response ? "pending" : "resolved";
                          return (
                            <FollowUpPanel
                              role="owner"
                              status={fuStatus}
                              controlType="declarative"
                              controlId={mCtrl.controlId}
                              statementLabel={mStmt.label}
                              assessorQuestion={question}
                              ownerResponse={response}
                              chatCount={chatEntries.length}
                              onOpenChat={
                                chatEntries.length > 0
                                  ? () =>
                                      openChat(
                                        mStmt.label,
                                        mCtrl.controlId,
                                        modalExec.id,
                                      )
                                  : undefined
                              }
                              actions={
                                isRaised && !fu?.followUpResponse ? (
                                  <FollowUpAction
                                    variant="primary"
                                    onClick={() => {
                                      setAssessModalOpen(false);
                                      setFollowUpModalExecId(modalExec.id);
                                      setFollowUpModalStmtLabel(mStmt.label);
                                      setFollowUpModalControlId(
                                        mCtrl.controlId,
                                      );
                                      setFollowUpModalSource("assess");
                                      setFollowUpModalOpen(true);
                                    }}
                                  >
                                    <Send className="size-3.5" /> Respond to
                                    Follow-Up
                                  </FollowUpAction>
                                ) : undefined
                              }
                            />
                          );
                        })()}
                        <div className="flex justify-end">
                          <button
                            onClick={() => setAssessModalOpen(false)}
                            className="px-5 py-2 rounded-lg text-[13px] font-bold bg-[#f1f5f9] dark:bg-white/[0.06] text-[#475569] dark:text-white/50 hover:bg-[#e2e8f0] dark:hover:bg-white/[0.08] transition-colors"
                          >
                            Close
                          </button>
                        </div>
                      </>
                    );
                  })()}
              </div>
            </ModalShell>
          );
        })()}

      {/* ══════ FOLLOW-UP MODAL ══════ */}
      {followUpModalOpen &&
        followUpModalExecId &&
        followUpModalStmtLabel &&
        (() => {
          const exec = executions.find((e) => e.id === followUpModalExecId);
          const followUp = exec ? getStatementFollowUp(exec.id) : null;
          if (!exec) return null;
          return (
            <ModalShell
              open
              onClose={() => setFollowUpModalOpen(false)}
              zIndexClass="z-[60]"
            >
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-[#e2e8f0] dark:border-white/[0.06] flex-shrink-0">
                <div className="flex items-center gap-2">
                  {followUpModalSource && (
                    <button
                      onClick={() => {
                        setFollowUpModalOpen(false);
                        if (followUpModalSource === "assess") {
                          setAssessModalOpen(true);
                        } else if (followUpModalSource === "auto") {
                          setAutoModalOpen(true);
                        }
                      }}
                      className="p-1.5 rounded-lg hover:bg-[#f1f5f9] dark:hover:bg-white/[0.06] transition-colors"
                      title="Back"
                    >
                      <ArrowLeft className="size-4 text-[#64748b] dark:text-white/40" />
                    </button>
                  )}
                  <AlertCircle className="size-5 text-amber-500" />
                  <span className="text-[15px] font-medium text-[#0f172a] dark:text-white">
                    Follow-Up Response
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => {
                      setFollowUpModalOpen(false);
                      if (exec) {
                        setChatExecId(exec.id);
                        setChatStmtLabel(followUpModalStmtLabel!);
                        setChatControlId(followUpModalControlId ?? "");
                        setChatSource("followup");
                        setChatOpen(true);
                      }
                    }}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-bold text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/20 hover:bg-amber-100 dark:hover:bg-amber-500/15 transition-all"
                  >
                    <MessageSquare className="size-3.5" /> View Chat
                  </button>
                  <button
                    onClick={() => setFollowUpModalOpen(false)}
                    className="p-1.5 rounded-lg hover:bg-[#f1f5f9] dark:hover:bg-white/[0.06] transition-colors"
                  >
                    <X className="size-5 text-[#64748b] dark:text-white/40" />
                  </button>
                </div>
              </div>
              {/* Body */}
              <div className="px-6 py-5 space-y-4 overflow-y-auto flex-1">
                {(exec.followUpQuestion || followUpModalQuestion) && (
                  <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-500/[0.06] border border-amber-200 dark:border-amber-500/20">
                    <p className="text-[12px] font-bold text-amber-800 dark:text-amber-300 mb-1">
                      Assessor Question
                    </p>
                    <p className="text-[13px] text-amber-900 dark:text-amber-200">
                      {exec.followUpQuestion || followUpModalQuestion}
                    </p>
                  </div>
                )}
                {(() => {
                  const fuKey = followUp?.id ?? exec.id;
                  if (savedFollowUps[fuKey]) {
                    return (
                      <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/20">
                        <CheckCircle2 className="size-3.5 text-emerald-500 flex-shrink-0" />
                        <p className="text-[12px] text-emerald-800 dark:text-emerald-300 flex-1">
                          {savedFollowUps[fuKey]}
                        </p>
                      </div>
                    );
                  }
                  const wordCount = countWords(followUpResponses[fuKey] ?? "");
                  return (
                    <>
                      <div>
                        <p className="text-[13px] font-semibold text-[#cbd5e1] dark:text-white/80 mb-2">
                          Your Response
                        </p>
                        <div className="rounded-lg border border-[#e2e8f0] dark:border-white/[0.07] bg-white dark:bg-[#131320] focus-within:ring-2 focus-within:ring-amber-500/20 focus-within:border-amber-500 transition-all overflow-hidden">
                          <textarea
                            value={followUpResponses[fuKey] ?? ""}
                            onChange={(e) =>
                              setFollowUpResponses((prev) => ({
                                ...prev,
                                [fuKey]: e.target.value,
                              }))
                            }
                            placeholder="Type your response (minimum 20 words)"
                            rows={4}
                            className="w-full text-[13px] px-4 py-2.5 outline-none bg-transparent placeholder:text-[#94a3b8] dark:placeholder:text-white/60 resize-y leading-relaxed text-[#0f172a] dark:text-white border-none focus:ring-0"
                          />
                          {followUp?.needsEvidence && (
                            <>
                              <div className="border-t border-[#e2e8f0] dark:border-white/[0.07]" />
                              <div className="flex items-center gap-2 px-3 py-2">
                                <button
                                  onClick={() =>
                                    openEvidencePicker(
                                      fuKey,
                                      followUpModalStmtLabel!,
                                    )
                                  }
                                  className="p-1.5 rounded-lg hover:bg-[#f1f5f9] dark:hover:bg-white/[0.06] transition-colors"
                                  title="Attach Evidence"
                                >
                                  <Paperclip className="size-4 text-[#64748b] dark:text-white/40 hover:text-amber-500 dark:hover:text-amber-400 transition-colors" />
                                </button>
                                {(followUpEvidenceIds[fuKey] ?? []).length >
                                  0 && (
                                  <span className="text-[11px] text-amber-600 dark:text-amber-400 font-medium">
                                    {followUpEvidenceIds[fuKey].length} file(s)
                                    attached
                                  </span>
                                )}
                                {!(followUpEvidenceIds[fuKey]?.length > 0) && (
                                  <span className="text-[11px] text-red-400 dark:text-red-400 font-medium">
                                    Evidence required
                                  </span>
                                )}
                              </div>
                            </>
                          )}
                        </div>
                        <p
                          className={`mt-1 text-[12px] ${wordCount >= MIN_WORDS ? "text-emerald-500" : "text-[#cbd5e1] dark:text-white/75"}`}
                        >
                          {wordCount}/{MIN_WORDS} words
                        </p>
                      </div>
                      {/* Attached files display */}
                      {(followUpEvidenceIds[fuKey] ?? []).length > 0 && (
                        <div className="space-y-1.5">
                          <p className="text-[13px] font-semibold text-[#cbd5e1] dark:text-white/80">
                            Attached Files
                          </p>
                          {followUpEvidenceIds[fuKey].map((ev) => (
                            <div
                              key={ev.id}
                              className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg bg-[#f8fafc] dark:bg-white/[0.03] border border-[#e2e8f0] dark:border-white/[0.06]"
                            >
                              <div className="flex items-center gap-2 min-w-0 flex-1">
                                <FileUp className="size-3.5 text-amber-500 flex-shrink-0" />
                                <span className="text-[12px] font-medium text-[#0f172a] dark:text-white/80 truncate">
                                  {ev.name}
                                </span>
                              </div>
                              <button
                                onClick={() => {
                                  setFollowUpEvidenceIds((prev) => ({
                                    ...prev,
                                    [fuKey]: (prev[fuKey] ?? []).filter(
                                      (e) => e.id !== ev.id,
                                    ),
                                  }));
                                }}
                                className="p-1 rounded hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors"
                                title="Remove"
                              >
                                <X className="size-3 text-red-400" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}

                      <div className="flex justify-end gap-2">
                        <button
                          onClick={async () => {
                            const responseText =
                              followUpResponses[fuKey]?.trim();
                            if (!responseText || !id) return;
                            const evIds = followUpEvidenceIds[fuKey]?.map(
                              (e) => e.id,
                            );
                            try {
                              await respondToFollowUp(
                                id,
                                exec.id,
                                responseText,
                                evIds,
                              );
                              setSavedFollowUps((prev) => ({
                                ...prev,
                                [fuKey]: responseText,
                              }));
                              setFollowUpResponses((prev) => ({
                                ...prev,
                                [fuKey]: "",
                              }));
                              setFollowUpEvidenceIds((prev) => ({
                                ...prev,
                                [fuKey]: [],
                              }));
                              setFollowUpModalOpen(false);
                              // refreshExecutionView refreshes controlsState
                              // so the Status/Action columns reflect the new
                              // FOLLOWUP_RESOLVED state immediately.
                              await Promise.all([
                                refreshExecutionView(),
                                fetchReviewStatus(),
                              ]);
                            } catch (err) {
                              console.error(
                                "Failed to send follow-up response:",
                                err,
                              );
                            }
                          }}
                          disabled={
                            !followUpResponses[fuKey]?.trim() ||
                            wordCount < MIN_WORDS ||
                            (followUp?.needsEvidence &&
                              !(followUpEvidenceIds[fuKey]?.length > 0))
                          }
                          className={`px-5 py-2 rounded-lg text-[12px] font-bold transition-all flex items-center gap-1.5 ${!followUpResponses[fuKey]?.trim() || wordCount < MIN_WORDS || (followUp?.needsEvidence && !(followUpEvidenceIds[fuKey]?.length > 0)) ? "bg-[#e2e8f0] dark:bg-white/[0.06] text-[#94a3b8] cursor-not-allowed" : "bg-amber-600 text-white hover:bg-amber-700"}`}
                        >
                          <Send className="size-3.5" /> Send
                        </button>
                      </div>
                    </>
                  );
                })()}
              </div>
            </ModalShell>
          );
        })()}

      {/* ── View Executed Statement Modal ── */}
      {viewModalStmt &&
        (() => {
          const stateUpper = (viewModalStmt.state ?? "").toUpperCase();
          const reviewStatusLower = (
            viewModalStmt.reviewStatus ?? ""
          ).toLowerCase();
          // review_status === "follow_up_resolved" overrides a lagged
          // state === "FOLLOWUP" (backend updates review_status first).
          const isFollowUpResolved =
            stateUpper === "FOLLOWUP_RESOLVED" ||
            reviewStatusLower === "follow_up_resolved";
          const isFollowUp = isFollowUpResolved || stateUpper === "FOLLOWUP";
          const displayState = isFollowUpResolved
            ? "FOLLOWUP_RESOLVED"
            : viewModalStmt.state;
          // Latest round drives the card. iterations[] is oldest → newest; the
          // assessor's most recent question can be on an earlier round if the
          // current one hasn't been re-reviewed yet, so reach back for it.
          const iters = viewModalStmt.iterations ?? [];
          const firstIter = iters.length > 0 ? iters[0] : null;
          // Latest reviewer question + the answer that follows it. Mapping
          // rule from the iterations[] flow doc: "reviewer_note in iter N is
          // answered by justification in iter N+1." So if the question is on
          // the last iteration and there's no next iteration yet, the
          // assessor has asked and the system owner has NOT yet responded —
          // cardAnswer is null and the panel shows its "no response yet"
          // placeholder, prompting the owner to use the Respond action.
          const lastQuestionIdx = (() => {
            for (let i = iters.length - 1; i >= 0; i--) {
              if (iters[i].reviewer_note) return i;
            }
            return -1;
          })();
          const cardQuestion =
            lastQuestionIdx >= 0
              ? (iters[lastQuestionIdx].reviewer_note ?? null)
              : null;
          const cardAnswer =
            lastQuestionIdx >= 0 && lastQuestionIdx + 1 < iters.length
              ? (iters[lastQuestionIdx + 1].justification ?? null)
              : null;
          const latestIter = iters.length > 0 ? iters[iters.length - 1] : null;
          // Original (first-round) submission, shown in the standalone
          // Justification card above the follow-up panel. NEVER reads from
          // the outer statement.justification — that field is ambiguous
          // across multi-round follow-ups per the iterations[] flow.
          const originalJustification = firstIter?.justification ?? null;
          return (
            <ModalShell
              open
              onClose={() => {
                setViewModalStmt(null);
                setViewModalCtrl(null);
              }}
              zIndexClass="z-[55]"
            >
              <div className="flex items-center justify-between px-5 py-3 border-b border-[#e2e8f0] dark:border-white/[0.06] bg-[#f8fafc] dark:bg-white/[0.02] flex-shrink-0">
                <div className="flex items-center gap-3">
                  {viewModalCtrl && (
                    <span className="text-xs font-mono font-bold text-indigo-600 dark:text-violet-400 bg-indigo-50 dark:bg-violet-500/10 px-2.5 py-1 rounded-lg">
                      {viewModalCtrl.controlId?.toUpperCase()}
                    </span>
                  )}
                  <span className="text-[14px] font-medium text-[#0f172a] dark:text-white">
                    {viewModalStmt.label?.toUpperCase()}
                  </span>
                  {displayState && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-bold bg-blue-50 dark:bg-blue-500/10 text-blue-700 dark:text-blue-400 border border-blue-200 dark:border-blue-500/20">
                      {displayState.replace(/_/g, " ")}
                    </span>
                  )}
                </div>
                <button
                  onClick={() => {
                    setViewModalStmt(null);
                    setViewModalCtrl(null);
                  }}
                  className="size-8 rounded-lg flex items-center justify-center hover:bg-[#e2e8f0] dark:hover:bg-white/[0.06] transition-colors"
                >
                  <X className="size-4 text-[#64748b]" />
                </button>
              </div>

              <div className="flex-1 min-h-0 overflow-y-auto p-5 space-y-4">
                {viewModalStmt.text && (
                  <p className="text-[13px] text-[#475569] dark:text-white/60 leading-relaxed">
                    {viewModalStmt.text}
                  </p>
                )}

                {/* Standalone Justification — non-follow-up states only.
                In follow-up flows the justification represents the system
                owner's response to the assessor's question, so it's rendered
                inside the follow-up card instead. */}
                {/* Standalone Justification — always shown. Read from the FIRST
                iteration (the executor's ORIGINAL submission). The latest
                round's answer renders inside the amber follow-up card as
                "Your Response". Never read from the outer
                statement.justification — that field is ambiguous across
                multi-round follow-ups per the iterations[] flow. */}
                <div>
                  <p className="text-[12px] font-bold text-[#0f172a] dark:text-white mb-1.5">
                    Justification
                  </p>
                  <div className="rounded-lg border border-[#e2e8f0] dark:border-white/[0.06] bg-[#f8fafc] dark:bg-white/[0.02] p-3">
                    <p className="text-[12px] text-[#334155] dark:text-white/70 whitespace-pre-wrap break-words leading-relaxed">
                      {originalJustification?.trim() || (
                        <span className="text-[#94a3b8] italic">
                          No justification provided.
                        </span>
                      )}
                    </p>
                  </div>
                </div>

                {/* Standalone Evidence — shown when there's no follow-up
                answer (iterations <= 1). Top-level evidence_files belongs
                to the latest round; when a follow-up answer exists
                (iters > 1) the evidence is the answer's evidence and
                renders inside the follow-up panel instead.
                A statement saved as plain "declarative" carries no evidence,
                so never surface leftover evidence_files for it (they can
                linger from an abandoned declarative + evidence attempt). */}
                {iters.length <= 1 &&
                  viewModalStmt.executionMethod !== "declarative" &&
                  (viewModalStmt.evidenceFiles ?? []).length > 0 && (
                    <div>
                      <p className="text-[12px] font-bold text-[#0f172a] dark:text-white mb-1.5">
                        Evidence ({viewModalStmt.evidenceFiles!.length})
                      </p>
                      <div className="space-y-2">
                        {viewModalStmt.evidenceFiles!.map((ev) => (
                          <div
                            key={ev.supporting_evidence_id}
                            className="flex items-center justify-between gap-3 rounded-lg border border-[#e2e8f0] dark:border-white/[0.06] bg-white dark:bg-white/[0.02] px-3 py-2"
                          >
                            <div className="flex items-center gap-2 min-w-0">
                              <FileText className="size-4 text-indigo-500 dark:text-violet-400 flex-shrink-0" />
                              <div className="min-w-0">
                                <p className="text-[12px] font-medium text-[#0f172a] dark:text-white truncate">
                                  {ev.title}
                                </p>
                                {ev.file_name && (
                                  <p className="text-[11px] text-[#64748b] dark:text-white/40 truncate">
                                    {ev.file_name}
                                  </p>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-1 flex-shrink-0">
                              <button
                                onClick={() =>
                                  handlePreview(
                                    ev.supporting_evidence_id,
                                    ev.title,
                                  )
                                }
                                className="p-1.5 rounded-lg hover:bg-[#e2e8f0] dark:hover:bg-white/[0.06] transition-colors"
                                title="Preview"
                              >
                                <Eye className="size-3.5 text-[#475569] dark:text-white/60" />
                              </button>
                              <button
                                onClick={() =>
                                  handleDownload(
                                    ev.supporting_evidence_id,
                                    ev.file_name ?? undefined,
                                  )
                                }
                                className="p-1.5 rounded-lg hover:bg-[#e2e8f0] dark:hover:bg-white/[0.06] transition-colors"
                                title="Download"
                              >
                                <Download className="size-3.5 text-[#475569] dark:text-white/60" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                {/* Unified follow-up panel (owner variant, declarative + D+E)
                — round-by-round display. When the round is PENDING (the
                assessor asked but iter N+1 doesn't exist yet), the panel
                renders an INLINE response form via responseSlot: empty
                textarea + evidence picker (if needs_evidence) + word
                counter. Submitting calls respondToFollowUp which creates
                iter N+1 on the backend.
                When RESOLVED (iter N+1 exists), the panel renders read-
                only ownerResponse = iter[N+1].justification. */}
                {isFollowUp &&
                  (() => {
                    // Submit goes through executeStatement(planId, statement_id, ...)
                    // — the SAME endpoint the initial save uses. The backend
                    // creates iteration N+1 and resolves the follow-up. We use
                    // viewModalStmt.id (the statement UUID) directly; no need
                    // to look up an execution id.
                    const fuKey = viewModalStmt.id;
                    const responseText = followUpResponses[fuKey] ?? "";
                    const wordCount = countWords(responseText);
                    const attachedEv = followUpEvidenceIds[fuKey] ?? [];
                    // needs_evidence per the iterations[] flow lives on the
                    // statement (mapped onto ExecutionStatement.needsEvidence
                    // from the latest iteration).
                    const stmtNeedsEvidence =
                      viewModalStmt.needsEvidence ?? false;
                    // No minimum-word requirement — any non-empty response is
                    // submittable. (Removed the 20-word gate per UX feedback.)
                    const canSubmit =
                      !!id &&
                      responseText.trim().length > 0 &&
                      (!stmtNeedsEvidence || attachedEv.length > 0) &&
                      !declFuSaving;
                    const submitInlineResponse = async () => {
                      if (!id) return;
                      setDeclFuSaving(true);
                      try {
                        // Mirror the automation re-save flow exactly: if evidence
                        // was attached, link it to the statement first (by
                        // statement LABEL — that's what the link API expects),
                        // then POST execution with the new justification. The
                        // backend creates iteration N+1 and resolves the
                        // follow-up. Same single POST endpoint that the initial
                        // declarative / D+E save uses:
                        //   POST /assessment-plans/{planId}/statements/{statementId}/execution
                        if (stmtNeedsEvidence && attachedEv.length > 0) {
                          await linkEvidenceToStatement(id, {
                            statementId: viewModalStmt.label,
                            evidenceIds: attachedEv.map((e) => e.id),
                          });
                        }
                        const executionMethod:
                          | "declarative"
                          | "declarative_evidence" = stmtNeedsEvidence
                          ? "declarative_evidence"
                          : "declarative";
                        await executeStatement(id, viewModalStmt.id, {
                          execution_method: executionMethod,
                          justification: responseText.trim(),
                        });
                        // Clear local form state and collapse.
                        setFollowUpResponses((prev) => ({
                          ...prev,
                          [fuKey]: "",
                        }));
                        setFollowUpEvidenceIds((prev) => ({
                          ...prev,
                          [fuKey]: [],
                        }));
                        setDeclFuRespondOpen(false);
                        await Promise.all([
                          refreshExecutionView(),
                          fetchReviewStatus(),
                        ]);
                        // Re-sync viewModalStmt from the refreshed controlsState so
                        // the panel re-renders with the new iteration (status
                        // flips to "Follow-Up Resolved", "YOUR RESPONSE" shows
                        // the just-submitted text, the Respond button is gone).
                        // The local viewModalStmt was set from the row data at
                        // open-time and would otherwise stay stale.
                        setControlsState((latest) => {
                          const refreshedStmt = latest
                            .flatMap((c) => c.statements ?? [])
                            .find(
                              (s) =>
                                s.id === viewModalStmt.id ||
                                s.label === viewModalStmt.label,
                            );
                          if (refreshedStmt) setViewModalStmt(refreshedStmt);
                          return latest;
                        });
                        useToastStore
                          .getState()
                          .showSuccess(
                            "Follow-Up Resolved",
                            "Re-saved for review.",
                          );
                      } catch (err: any) {
                        const apiMsg =
                          err?.responseBody?.errors?.[0]?.detail ||
                          err?.responseBody?.errors?.[0]?.title ||
                          err?.message;
                        useToastStore
                          .getState()
                          .showError(
                            "Submit Failed",
                            apiMsg ||
                              "Failed to submit the follow-up response.",
                          );
                        console.error(
                          "Declarative follow-up submit failed:",
                          err,
                        );
                      } finally {
                        setDeclFuSaving(false);
                      }
                    };
                    return (
                      <FollowUpPanel
                        role="owner"
                        status={isFollowUpResolved ? "resolved" : "pending"}
                        controlType={
                          (viewModalStmt.evidenceFiles ?? []).length > 0
                            ? "declarative_evidence"
                            : "declarative"
                        }
                        controlId={viewModalCtrl?.controlId}
                        statementLabel={viewModalStmt.label}
                        assessorQuestion={cardQuestion}
                        // Read-only response = iter[lastQuestionIdx + 1].justification.
                        // Null when iter N+1 doesn't exist yet → inline form
                        // takes its place via responseSlot.
                        ownerResponse={cardAnswer}
                        evidence={[]}
                        chatCount={iters.length}
                        onOpenChat={
                          iters.length > 1
                            ? () =>
                                openChatFromView(
                                  viewModalStmt.label,
                                  viewModalCtrl?.controlId ?? "",
                                  latestIter?.statement_execution_id ??
                                    viewModalStmt.id,
                                )
                            : undefined
                        }
                        responseSlot={
                          !isFollowUpResolved && declFuRespondOpen ? (
                            <div className="rounded-lg border border-amber-200 dark:border-amber-500/25 bg-white dark:bg-white/[0.04] focus-within:ring-2 focus-within:ring-amber-500/20 focus-within:border-amber-500 transition-all overflow-hidden">
                              <textarea
                                value={responseText}
                                onChange={(e) =>
                                  setFollowUpResponses((prev) => ({
                                    ...prev,
                                    [fuKey]: e.target.value,
                                  }))
                                }
                                placeholder="Explain what changed or how you addressed the reviewer's note"
                                rows={3}
                                className="w-full text-[13px] px-3 py-2.5 outline-none bg-transparent border-none focus:ring-0 resize-y text-[#0f172a] dark:text-white placeholder:text-[#94a3b8] dark:placeholder:text-white/50"
                              />
                              {stmtNeedsEvidence && (
                                <div className="flex items-center gap-2 px-3 py-2 border-t border-amber-200/70 dark:border-amber-500/20">
                                  <button
                                    onClick={() =>
                                      openEvidencePicker(
                                        fuKey,
                                        viewModalStmt.label,
                                      )
                                    }
                                    className="flex-shrink-0 p-1 rounded hover:bg-amber-100/60 dark:hover:bg-amber-500/10 transition-colors"
                                    title="Attach Evidence"
                                  >
                                    <Paperclip className="size-4 text-amber-600 dark:text-amber-400" />
                                  </button>
                                  {attachedEv.length > 0 ? (
                                    <div className="flex items-center gap-1.5 flex-wrap flex-1 min-w-0">
                                      {attachedEv.map((ev) => (
                                        <span
                                          key={ev.id}
                                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border border-emerald-200/60 dark:border-emerald-500/20 max-w-[220px]"
                                        >
                                          <FileText className="size-3 flex-shrink-0" />
                                          <span className="truncate">
                                            {ev.name}
                                          </span>
                                          <button
                                            onClick={() =>
                                              setFollowUpEvidenceIds(
                                                (prev) => ({
                                                  ...prev,
                                                  [fuKey]: (
                                                    prev[fuKey] ?? []
                                                  ).filter(
                                                    (x) => x.id !== ev.id,
                                                  ),
                                                }),
                                              )
                                            }
                                            className="ml-0.5 p-0.5 rounded hover:bg-emerald-100 dark:hover:bg-emerald-500/20"
                                          >
                                            <X className="size-2.5" />
                                          </button>
                                        </span>
                                      ))}
                                    </div>
                                  ) : (
                                    <span className="text-[12px] font-medium text-red-500 dark:text-red-400">
                                      Evidence required
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                          ) : undefined
                        }
                        actions={
                          !isFollowUpResolved ? (
                            declFuRespondOpen ? (
                              <>
                                <FollowUpAction
                                  variant="secondary"
                                  onClick={() => {
                                    setFollowUpResponses((prev) => ({
                                      ...prev,
                                      [fuKey]: "",
                                    }));
                                    setFollowUpEvidenceIds((prev) => ({
                                      ...prev,
                                      [fuKey]: [],
                                    }));
                                    setDeclFuRespondOpen(false);
                                  }}
                                >
                                  Cancel
                                </FollowUpAction>
                                <FollowUpAction
                                  variant="primary"
                                  disabled={!canSubmit}
                                  onClick={submitInlineResponse}
                                >
                                  {declFuSaving ? (
                                    <Loader2 className="size-3.5 animate-spin" />
                                  ) : (
                                    <Send className="size-3.5" />
                                  )}
                                  {declFuSaving
                                    ? "Submitting"
                                    : "Submit Response"}
                                </FollowUpAction>
                              </>
                            ) : (
                              <FollowUpAction
                                variant="primary"
                                onClick={() => setDeclFuRespondOpen(true)}
                              >
                                <Send className="size-3.5" /> Respond
                              </FollowUpAction>
                            )
                          ) : undefined
                        }
                        defaultExpanded
                      />
                    );
                  })()}
              </div>
            </ModalShell>
          );
        })()}

      {/* ── Automation Results Modal ── */}
      {autoModalOpen &&
        autoModalCtrl &&
        (() => {
          // A statement is awaiting the system owner's follow-up response when its
          // execution-view state === "FOLLOWUP" (reviewer bounced it back). All the
          // data we need (reviewer note, needs_evidence, ids) lives on that statement.
          const followUpStmt =
            (autoModalCtrl.executionViewStatements ?? []).find(
              (s) => (s.state ?? "").toUpperCase() === "FOLLOWUP",
            ) ?? null;
          const isFollowUp = !!followUpStmt;
          const followUpNote =
            followUpStmt?.reviewer_note ?? followUpStmt?.note ?? null;
          const followUpNeedsEvidence = followUpStmt?.needs_evidence ?? false;
          // After re-submitting, the statement sits in FOLLOWUP_RESOLVED ("Awaiting
          // Re-Review"). Surface what the owner submitted (justification + evidence).
          const resolvedStmt =
            (autoModalCtrl.executionViewStatements ?? []).find(
              (s) => (s.state ?? "").toUpperCase() === "FOLLOWUP_RESOLVED",
            ) ?? null;
          // Owner's submitted response is ALWAYS read from the latest iteration's
          // justification, never the outer statement.justification — per the
          // iterations[] flow, the outer field is ambiguous across rounds.
          const resolvedLatestIter =
            resolvedStmt?.iterations?.[resolvedStmt.iterations.length - 1] ??
            null;
          const submittedJustification =
            resolvedLatestIter?.justification?.trim() || null;
          const submittedEvidence = resolvedStmt?.evidence_files ?? [];
          const hasSubmittedResponse =
            Boolean(submittedJustification) || submittedEvidence.length > 0;
          const closeAutoModal = () => {
            setAutoModalOpen(false);
            setAutoRespondOpen(false);
            setAutoFuFile(null);
            setAutoFuJustification("");
          };
          return (
            <ModalShell
              open
              onClose={closeAutoModal}
              zIndexClass="z-[55]"
              variant="wide"
            >
              {/* Header */}
              <div className="flex items-center justify-between px-5 py-3 border-b border-[#e2e8f0] dark:border-white/[0.06] bg-[#f8fafc] dark:bg-white/[0.02] flex-shrink-0">
                <div className="flex items-center gap-3">
                  <span className="text-xs font-mono font-bold text-indigo-600 dark:text-violet-400 bg-indigo-50 dark:bg-violet-500/10 px-2.5 py-1 rounded-lg">
                    {autoModalCtrl.controlId?.toUpperCase()}
                  </span>
                  <span className="text-[14px] font-medium text-[#0f172a] dark:text-white">
                    {autoModalCtrl.controlDescription || "Automation Results"}
                  </span>
                  <span
                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[12px] font-bold ${
                      autoModalCtrl.opaOverallStatus === "passed"
                        ? "bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
                        : "bg-red-50 dark:bg-red-500/10 text-red-600 dark:text-red-400"
                    }`}
                  >
                    {autoModalCtrl.opaOverallStatus === "passed" ? (
                      <CheckCircle2 className="size-3" />
                    ) : (
                      <XCircle className="size-3" />
                    )}
                    {autoModalCtrl.opaOverallStatus === "passed"
                      ? "Passed"
                      : "Failed"}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={closeAutoModal}
                    className="size-8 rounded-lg flex items-center justify-center hover:bg-[#e2e8f0] dark:hover:bg-white/[0.06] transition-colors"
                  >
                    <X className="size-4 text-[#64748b]" />
                  </button>
                </div>
              </div>

              
              <div className="flex-1 min-h-0 overflow-y-auto px-5 pb-5 space-y-3">
                {(() => {
                  const firstStmt = (autoModalCtrl.executionViewStatements ??
                    [])[0];
                  return firstStmt ? (
                    <div className="pt-5 space-y-2">
                      <p className="text-[12px] font-bold text-[#0f172a] dark:text-white">
                        {firstStmt.statement_label}
                      </p>
                      <p className="text-[13px] text-[#334155] dark:text-white/80 leading-relaxed">
                        {firstStmt.statement_description}
                      </p>
                    </div>
                  ) : null;
                })()}

                {/* Unified follow-up panel (owner variant, automation).
                  Modes:
                  - Pending response (isFollowUp, !autoRespondOpen): shows
                    assessor question + "Respond" action.
                  - Responding (isFollowUp, autoRespondOpen): inline textarea
                    + evidence picker via responseSlot, plus Re-submit/Cancel.
                  - Submitted (resolved): read-only justification + evidence. */}
                {isFollowUp && followUpStmt && (
                  <FollowUpPanel
                    role="owner"
                    status="pending"
                    controlType={
                      followUpNeedsEvidence
                        ? "declarative_evidence"
                        : "automation"
                    }
                    controlId={autoModalCtrl.controlId}
                    statementLabel={followUpStmt.statement_label}
                    assessorQuestion={followUpNote}
                    onOpenChat={() =>
                      openChat(
                        followUpStmt.statement_label,
                        autoModalCtrl.controlId,
                        followUpStmt.statement_execution_id ?? "",
                      )
                    }
                    defaultExpanded={autoRespondOpen}
                    responseSlot={
                      autoRespondOpen ? (
                        <div className="rounded-lg border border-amber-200 dark:border-amber-500/25 bg-white dark:bg-white/[0.04] focus-within:ring-2 focus-within:ring-amber-500/20 focus-within:border-amber-500 transition-all overflow-hidden">
                          <textarea
                            value={autoFuJustification}
                            onChange={(e) =>
                              setAutoFuJustification(e.target.value)
                            }
                            placeholder={
                              followUpNeedsEvidence
                                ? "Explain what changed or how you addressed the reviewer's note"
                                : "Re-confirm this control against the latest scan, then re-submit."
                            }
                            rows={3}
                            className="w-full text-[13px] px-3 py-2.5 outline-none bg-transparent border-none focus:ring-0 resize-y text-[#0f172a] dark:text-white placeholder:text-[#94a3b8] dark:placeholder:text-white/50"
                          />
                          {followUpNeedsEvidence && (
                            <div className="flex items-center gap-2 px-3 py-2 border-t border-amber-200/70 dark:border-amber-500/20">
                              <label
                                className="flex-shrink-0 cursor-pointer p-1 rounded hover:bg-amber-100/60 dark:hover:bg-amber-500/10 transition-colors"
                                title="Attach evidence"
                              >
                                <Paperclip className="size-4 text-amber-600 dark:text-amber-400" />
                                <input
                                  type="file"
                                  className="hidden"
                                  onChange={(e) => {
                                    const f = e.target.files?.[0];
                                    if (f) setAutoFuFile(f);
                                    e.target.value = "";
                                  }}
                                />
                              </label>
                              {autoFuFile ? (
                                <>
                                  <FileText className="size-3.5 text-emerald-600 dark:text-emerald-400 flex-shrink-0" />
                                  <span className="text-[12px] font-medium text-emerald-700 dark:text-emerald-400 truncate">
                                    {autoFuFile.name}
                                  </span>
                                  <button
                                    onClick={() => setAutoFuFile(null)}
                                    className="flex-shrink-0 ml-auto size-5 rounded flex items-center justify-center text-[#64748b] hover:bg-[#e2e8f0] dark:hover:bg-white/[0.08] transition-colors"
                                  >
                                    <X className="size-3" />
                                  </button>
                                </>
                              ) : (
                                <span className="text-[12px] font-medium text-red-500 dark:text-red-400">
                                  Evidence required
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      ) : undefined
                    }
                    actions={
                      autoRespondOpen ? (
                        <>
                          <FollowUpAction
                            variant="secondary"
                            onClick={() => {
                              setAutoRespondOpen(false);
                              setAutoFuFile(null);
                              setAutoFuJustification("");
                            }}
                          >
                            Cancel
                          </FollowUpAction>
                          <FollowUpAction
                            variant="primary"
                            disabled={
                              autoFuSaving ||
                              (followUpNeedsEvidence && !autoFuFile)
                            }
                            onClick={() =>
                              handleAutoFollowUpResave(
                                followUpStmt.statement_id,
                                followUpStmt.statement_label,
                                followUpNeedsEvidence,
                                autoFuJustification,
                              )
                            }
                          >
                            {autoFuSaving ? (
                              <Loader2 className="size-3.5 animate-spin" />
                            ) : (
                              <Send className="size-3.5" />
                            )}
                            {autoFuSaving ? "Submitting" : "Submit Response"}
                          </FollowUpAction>
                        </>
                      ) : (
                        <FollowUpAction
                          variant="primary"
                          onClick={() => setAutoRespondOpen(true)}
                        >
                          <Send className="size-3.5" /> Respond
                        </FollowUpAction>
                      )
                    }
                  />
                )}

                {/* Submitted response (read-only) — shown once re-submitted
                  and the FOLLOWUP statement has been dropped (Awaiting Re-Review). */}
                {!isFollowUp && hasSubmittedResponse && (
                  <FollowUpPanel
                    role="owner"
                    status="resolved"
                    controlType={
                      submittedEvidence.length > 0
                        ? "declarative_evidence"
                        : "automation"
                    }
                    controlId={autoModalCtrl.controlId}
                    statementLabel={resolvedStmt?.statement_label}
                    ownerResponse={submittedJustification}
                    evidence={submittedEvidence.map((ev) => ({
                      id: ev.supporting_evidence_id,
                      name: ev.title || ev.file_name || "Evidence",
                      onPreview: () =>
                        handlePreview(
                          ev.supporting_evidence_id,
                          ev.title || ev.file_name,
                        ),
                    }))}
                    defaultExpanded
                  />
                )}

                <AutomationChecksView control={autoModalCtrl} />
              </div>

              {/* Footer — first save (NEW state) of an automated control */}
              {(() => {
                const isAutomated =
                  (autoModalCtrl.executionMode ?? "").toUpperCase() ===
                  "AUTOMATED";
                if (!isAutomated) return null;

                const viewStmts = autoModalCtrl.executionViewStatements ?? [];
                // First-save only. FOLLOWUP re-save is handled in the follow-up section above.
                const newStmts = viewStmts.filter(
                  (s) => (s.state ?? "NEW").toUpperCase() === "NEW",
                );
                if (newStmts.length === 0) return null;

                const savable = newStmts.filter(
                  (s) => (s.assets?.length ?? 0) > 0,
                );
                if (savable.length === 0) {
                  return (
                    <div className="flex items-center justify-between gap-3 px-5 py-3 border-t border-[#e2e8f0] dark:border-white/[0.06] bg-[#f8fafc] dark:bg-white/[0.02] flex-shrink-0">
                      <span className="inline-flex items-center gap-1.5 text-[12px] font-medium text-amber-700 dark:text-amber-400">
                        <AlertCircle className="size-3.5" /> No scan data
                        available yet — nothing to save.
                      </span>
                      <button
                        onClick={() => setAutoModalOpen(false)}
                        className="px-4 py-2 rounded-lg text-[12px] font-medium text-[#475569] dark:text-white/70 hover:bg-[#e2e8f0] dark:hover:bg-white/[0.06] transition-colors"
                      >
                        Close
                      </button>
                    </div>
                  );
                }

                return (
                  <div className="flex items-center justify-end gap-3 px-5 py-3 border-t border-[#e2e8f0] dark:border-white/[0.06] bg-[#f8fafc] dark:bg-white/[0.02] flex-shrink-0">
                    <button
                      onClick={() => setAutoModalOpen(false)}
                      disabled={savingAutoModal}
                      className="px-4 py-2 rounded-lg text-[12px] font-medium text-[#475569] dark:text-white/70 hover:bg-[#e2e8f0] dark:hover:bg-white/[0.06] transition-colors disabled:opacity-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSaveAutomated}
                      disabled={savingAutoModal}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-[12px] font-bold text-white bg-indigo-600 dark:bg-violet-600 hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {savingAutoModal ? (
                        <Loader2 className="size-3.5 animate-spin" />
                      ) : (
                        <Check className="size-3.5" />
                      )}
                      {savingAutoModal ? "Saving" : "Save"}
                    </button>
                  </div>
                );
              })()}
            </ModalShell>
          );
        })()}

      {/* ── Evidence Preview Modal ── */}
      {preview && (
        <ModalShell
          open
          onClose={closePreview}
          zIndexClass="z-[80]"
          variant="preview"
        >
          <FilePreviewHeader
            title={preview.title}
            fileName={preview.fileName}
            downloadUrl={preview.downloadUrl}
            onClose={closePreview}
          />
          <FilePreviewBody url={preview.url} fileName={preview.fileName} />
        </ModalShell>
      )}

      {/* ── Delete Confirmation Dialog ── */}
      {deleteConfirm && (
        <ModalShell
          open
          onClose={() => !deleteLoading && setDeleteConfirm(null)}
          zIndexClass="z-[70]"
        >
          <div className="px-6 pt-6 pb-2">
            <div className="flex items-center gap-3 mb-3">
              <div className="size-10 rounded-full bg-red-50 dark:bg-red-500/10 flex items-center justify-center">
                <Trash2 className="size-5 text-red-500" />
              </div>
              <h3 className="text-[16px] font-medium text-[#0f172a] dark:text-white">
                Delete Evidence
              </h3>
            </div>
            <p className="text-[13px] text-[#64748b] dark:text-white/60 leading-relaxed">
              Are you sure you want to delete{" "}
              <strong className="text-[#0f172a] dark:text-white">
                {deleteConfirm.evidenceTitle}
              </strong>
              ? This will unlink it from the statement and permanently remove
              the evidence. This action cannot be undone.
            </p>
          </div>
          <div className="flex justify-end gap-3 px-6 py-4">
            <button
              onClick={() => setDeleteConfirm(null)}
              disabled={deleteLoading}
              className="px-4 py-2 rounded-lg text-[13px] font-medium text-[#cbd5e1] dark:text-white/75 hover:bg-[#f1f5f9] dark:hover:bg-white/[0.06] transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleDeleteEvidence}
              disabled={deleteLoading}
              className="px-4 py-2 rounded-lg text-[13px] font-medium text-white bg-red-500 hover:bg-red-600 transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              {deleteLoading && (
                <div className="size-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              )}
              Delete
            </button>
          </div>
        </ModalShell>
      )}

      {/* ── Follow-Up Chat Modal ── */}
      {chatOpen &&
        (() => {
          const ri = reviewItems.find((r) => r.execution?.id === chatExecId);
          const execution = executions.find((e) => e.id === chatExecId);
          const chatFu = execution ? getStatementFollowUp(execution.id) : null;
          const chatFuKey = chatFu?.id ?? execution?.id ?? "";
          // Source the full Q&A timeline from execution-view iterations. Look
          // the statement up by label; falls back to [] if not found.
          let chatIterations: AssessmentExecutionViewIteration[] = [];
          for (const ctrl of controlsState) {
            const stmt = ctrl.statements.find((s) => s.label === chatStmtLabel);
            if (stmt) {
              chatIterations = stmt.iterations ?? [];
              break;
            }
          }

          return (
            <FollowUpChatModal
              open={chatOpen}
              onClose={() => {
                setChatOpen(false);
                // Restore the source modal when chat closes
                if (chatSource === "assess") setAssessModalOpen(true);
                else if (chatSource === "auto") setAutoModalOpen(true);
                else if (chatSource === "view" && chatReturnViewRef.current) {
                  setViewModalStmt(chatReturnViewRef.current.stmt);
                  setViewModalCtrl(chatReturnViewRef.current.ctrl);
                }
                setChatSource(null);
              }}
              statementId={chatStmtLabel}
              controlId={chatControlId}
              reviewHistory={buildChatHistory(
                ri?.reviewHistory ?? [],
                ri?.execution,
                ri?.currentReviewStatus,
              )}
              iterations={chatIterations}
              currentFollowUpQuestion={execution?.followUpQuestion}
              currentStatus={
                ri?.currentReviewStatus ?? execution?.reviewStatus ?? "pending"
              }
              perspective="execution"
              onBack={() => {
                setChatOpen(false);
                // Restore the source modal when going back
                if (chatSource === "assess") {
                  setAssessModalOpen(true);
                } else if (chatSource === "auto") {
                  setAutoModalOpen(true);
                } else if (chatSource === "view" && chatReturnViewRef.current) {
                  setViewModalStmt(chatReturnViewRef.current.stmt);
                  setViewModalCtrl(chatReturnViewRef.current.ctrl);
                } else if (execution) {
                  setFollowUpModalExecId(execution.id);
                  setFollowUpModalStmtLabel(chatStmtLabel);
                  setFollowUpModalControlId(chatControlId);
                  setFollowUpModalOpen(true);
                }
                setChatSource(null);
              }}
              needsEvidence={!!chatFu?.needsEvidence}
              onAttachEvidence={() => {
                if (chatFuKey) {
                  openEvidencePicker(chatFuKey, chatStmtLabel);
                }
              }}
              attachedEvidenceCount={
                followUpEvidenceIds[chatFuKey]?.length ?? 0
              }
              onSendResponse={
                execution?.reviewStatus === "follow_up_raised" &&
                execution?.editable &&
                id
                  ? async (text: string) => {
                      const fu = execution
                        ? getStatementFollowUp(execution.id)
                        : null;
                      if (fu && id && execution) {
                        await respondToFollowUp(
                          id,
                          execution.id,
                          text,
                          undefined,
                          execution.determination,
                          execution.justification,
                        );
                        setSavedFollowUps((prev) => ({
                          ...prev,
                          [fu.id]: text,
                        }));
                        await Promise.all([
                          refreshExecutionView(),
                          fetchReviewStatus(),
                        ]);
                      }
                    }
                  : undefined
              }
            />
          );
        })()}

      {/* ── Follow-Up Evidence Picker Modal ── */}
      {fuPickerOpen && fuPickerReviewId && (
        <ModalShell
          open
          onClose={() => setFuPickerOpen(false)}
          zIndexClass="z-[65]"
        >
          <div className="p-6 space-y-4 overflow-y-auto flex-1 flex flex-col">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-medium text-[#0f172a] dark:text-white">
                Attach Evidence to Follow-Up
              </h3>
              <button
                onClick={() => setFuPickerOpen(false)}
                className="size-7 rounded-lg flex items-center justify-center text-[#64748b] hover:bg-[#f1f5f9] dark:hover:bg-white/[0.06] transition-colors"
              >
                <X className="size-4" />
              </button>
            </div>

            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-3.5 text-[#94a3b8]" />
              <input
                type="text"
                value={fuSearchQuery}
                onChange={(e) => {
                  setFuSearchQuery(e.target.value);
                  setFuListPage(0);
                }}
                placeholder="Search evidence by title"
                className="w-full text-xs bg-white dark:bg-white/[0.04] rounded-lg pl-9 pr-3 py-2 outline-none border border-[#e2e8f0] dark:border-white/[0.08] focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 text-[#0f172a] dark:text-white placeholder:text-[#94a3b8]"
              />
            </div>

            {/* Upload new evidence */}
            <div
              className={`flex flex-col gap-2 px-4 py-6 rounded-xl border-2 border-dashed transition-all ${fuUploading ? "opacity-60 border-gray-300 dark:border-white/10" : "border-amber-300 dark:border-amber-500/30"} ${fuJustUploaded.length === 0 && fuLinkedEvidenceIds.size === 0 && !fuUploading ? "hover:border-amber-400 dark:hover:border-amber-500/50 hover:bg-amber-50/50 dark:hover:bg-amber-500/5" : ""}`}
            >
              {/* Dropzone click target — hidden when files already uploaded or linked to
                  prevent accidental re-upload; users can remove an item first. */}
              {fuJustUploaded.length === 0 && fuLinkedEvidenceIds.size === 0 ? (
                <label
                  className={`flex flex-col items-center justify-center gap-2 ${fuUploading ? "cursor-not-allowed" : "cursor-pointer"}`}
                >
                  {fuUploading ? (
                    <Loader2 className="size-5 animate-spin text-amber-500" />
                  ) : (
                    <Upload className="size-5 text-amber-500" />
                  )}
                  <p className="text-[12px] font-medium text-amber-600 dark:text-amber-400">
                    {fuUploading
                      ? "Uploading"
                      : "Click to upload an evidence file"}
                  </p>
                  {!fuUploading && (
                    <p className="text-[10px] text-[#475569] dark:text-white/60">
                      Supported: PDF, JSON, CSV, XLSX, TXT, PNG, JPG, XML, YAML
                    </p>
                  )}
                  <input
                    type="file"
                    accept=".pdf,.json,.csv,.xlsx,.xls,.doc,.docx,.txt,.png,.jpg,.jpeg,.xml,.yaml,.yml"
                    className="hidden"
                    disabled={fuUploading}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleFuUploadEvidence(file);
                      e.target.value = "";
                    }}
                  />
                </label>
              ) : (
                <>
                  {fuJustUploaded.map((ev) => (
                    <div
                      key={ev.id}
                      className="flex items-center justify-between px-3 py-2 rounded-lg bg-emerald-50/60 dark:bg-emerald-500/10 border border-emerald-300 dark:border-emerald-500/20"
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <FileText className="size-3.5 text-emerald-600 dark:text-emerald-400 flex-shrink-0" />
                        <span className="text-[12px] font-medium text-emerald-800 dark:text-emerald-300 truncate">
                          {ev.fileName || ev.title}
                        </span>
                        {ev.fileSize != null && (
                          <span className="text-[11px] text-emerald-600/80 dark:text-emerald-400/70 flex-shrink-0">
                            {(ev.fileSize / 1024).toFixed(1)} KB
                          </span>
                        )}
                      </div>
                      <button
                        onClick={() =>
                          setFuJustUploaded((prev) =>
                            prev.filter((x) => x.id !== ev.id),
                          )
                        }
                        className="p-1 rounded text-emerald-500 hover:text-red-500 transition-colors flex-shrink-0"
                        title="Remove"
                      >
                        <X className="size-3.5" />
                      </button>
                    </div>
                  ))}
                </>
              )}
            </div>

            {/* Evidence list */}
            <div className="flex-1 overflow-y-auto space-y-1 min-h-0 pr-1 fu-evidence-list">
              {fuPickerLoading ? (
                <div className="flex items-center justify-center py-8 text-xs text-[#64748b]">
                  <Loader2 className="size-4 animate-spin mr-2" /> Loading
                  evidence
                </div>
              ) : fuAvailableEvidence.length === 0 ? (
                <div className="text-center py-8 text-xs text-[#64748b] dark:text-white/40">
                  No evidence found
                </div>
              ) : (
                (() => {
                  const filtered = fuAvailableEvidence.filter(
                    (e) =>
                      !fuSearchQuery ||
                      e.title
                        ?.toLowerCase()
                        .includes(fuSearchQuery.toLowerCase()) ||
                      e.fileName
                        ?.toLowerCase()
                        .includes(fuSearchQuery.toLowerCase()),
                  );
                  const perPage = 10;
                  const totalPages = Math.max(
                    1,
                    Math.ceil(filtered.length / perPage),
                  );
                  const safePage = Math.min(fuListPage, totalPages - 1);
                  const pageStart = safePage * perPage;
                  const pageItems = filtered.slice(
                    pageStart,
                    pageStart + perPage,
                  );
                  return (
                    <>
                      {pageItems.map((ev) => {
                        const isLinked = fuLinkedEvidenceIds.has(ev.id);
                        const isSelected = fuSelectedIds.has(ev.id);
                        return (
                          <label
                            key={ev.id}
                            className={`flex items-start gap-3 px-3 py-2.5 rounded-lg border transition-all cursor-pointer ${
                              isLinked
                                ? "cursor-not-allowed bg-emerald-50/40 dark:bg-emerald-500/[0.06] border-emerald-200 dark:border-emerald-500/20"
                                : isSelected
                                  ? "bg-amber-50 dark:bg-amber-500/10 border-amber-300 dark:border-amber-500/30"
                                  : "bg-white dark:bg-white/[0.03] border-[#e2e8f0] dark:border-white/[0.08] hover:border-amber-300 dark:hover:border-amber-500/30"
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={isSelected || isLinked}
                              disabled={isLinked}
                              onChange={() => {
                                if (isLinked) return;
                                setFuSelectedIds((prev) => {
                                  const next = new Set(prev);
                                  if (next.has(ev.id)) next.delete(ev.id);
                                  else next.add(ev.id);
                                  return next;
                                });
                              }}
                              className="size-3.5 mt-1 rounded border-gray-300 text-amber-600 focus:ring-amber-500/20 flex-shrink-0"
                            />
                            <div className="flex-1 min-w-0 space-y-1">
                              <p className="text-[13px] font-medium text-[#0f172a] dark:text-white break-words">
                                {ev.title}
                              </p>
                              {ev.fileName && (
                                <p className="text-[12px] font-normal text-[#64748b] dark:text-white/60 break-all flex items-start gap-1">
                                  <Paperclip className="size-3 mt-0.5 flex-shrink-0" />
                                  <span className="break-all">
                                    {ev.fileName}
                                  </span>
                                </p>
                              )}
                              {ev.tags?.length > 0 && (
                                <p className="text-[12px] font-normal text-[#94a3b8] dark:text-white/50 break-words">
                                  <span className="font-medium text-[#0f172a] dark:text-white/70">
                                    Tags:
                                  </span>{" "}
                                  {ev.tags.join(", ")}
                                </p>
                              )}
                            </div>
                            <div className="flex-shrink-0 flex items-center gap-2">
                              {isLinked && (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold text-emerald-700 dark:text-emerald-300 bg-emerald-100 dark:bg-emerald-500/20 border border-emerald-300 dark:border-emerald-500/40 tracking-wide">
                                  <Check className="size-3" /> Linked
                                </span>
                              )}
                              {ev.hasFile && (
                                <>
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      handlePreview(ev.id, ev.title);
                                    }}
                                    title="Preview"
                                    className="p-1.5 rounded-md text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-500/10 hover:bg-indigo-100 dark:hover:bg-indigo-500/20 transition-colors"
                                  >
                                    <Eye className="size-3.5" />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      handleDownload(ev.id, ev.title);
                                    }}
                                    title="Download"
                                    className="p-1.5 rounded-md text-sky-600 dark:text-sky-400 bg-sky-50 dark:bg-sky-500/10 hover:bg-sky-100 dark:hover:bg-sky-500/20 transition-colors"
                                  >
                                    <Download className="size-3.5" />
                                  </button>
                                </>
                              )}
                            </div>
                          </label>
                        );
                      })}
                      {totalPages > 1 && (
                        <div className="flex items-center justify-between pt-2">
                          <button
                            onClick={() =>
                              setFuListPage((p) => Math.max(0, p - 1))
                            }
                            disabled={safePage === 0}
                            className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-[11px] font-medium text-[#475569] dark:text-white/60 bg-[#f1f5f9] dark:bg-white/[0.04] hover:bg-[#e2e8f0] dark:hover:bg-white/[0.08] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                          >
                            <ChevronLeft className="size-3.5" /> Previous
                          </button>
                          <span className="text-[11px] text-[#64748b] dark:text-white/40">
                            Page {safePage + 1} of {totalPages}
                          </span>
                          <button
                            onClick={() =>
                              setFuListPage((p) =>
                                Math.min(totalPages - 1, p + 1),
                              )
                            }
                            disabled={safePage >= totalPages - 1}
                            className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-[11px] font-medium text-[#475569] dark:text-white/60 bg-[#f1f5f9] dark:bg-white/[0.04] hover:bg-[#e2e8f0] dark:hover:bg-white/[0.08] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                          >
                            Next <ChevronRight className="size-3.5" />
                          </button>
                        </div>
                      )}
                    </>
                  );
                })()
              )}
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end gap-2 pt-2 border-t border-[#e2e8f0] dark:border-white/[0.08]">
              <button
                onClick={() => setFuPickerOpen(false)}
                className="px-3 py-1.5 text-xs rounded-lg text-[#64748b] hover:bg-[#f1f5f9] dark:hover:bg-white/[0.06] transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmEvidencePicker}
                className="px-4 py-1.5 text-xs font-bold rounded-lg bg-amber-600 text-white hover:bg-amber-700 transition-colors shadow-sm"
              >
                Attach Selected
              </button>
            </div>
          </div>
        </ModalShell>
      )}

      <ValidationDetailsDrawer
        open={validationDrawerOpen}
        onClose={closeValidationDrawer}
        loading={validationLoading}
        error={validationError}
        data={validationData}
        meta={validationMeta}
      />
    </PageShell>
  );
}
